// Zoom Room Software: https://zoom.us/download
// Zoom API documentation: https://marketplace.zoom.us/docs/guides/zoom-rooms/zoom-rooms-cli
// Forum: https://devforum.zoom.us/c/zoom-rooms-control-system-api
// Supported HDMI capture devices: https://support.zoom.us/hc/en-us/articles/201504265-Wired-HDMI-Screen-Share-via-Capture-Device
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import moment from 'moment';
import { Sutl } from '../../../sutl';
import { Timeouts } from './framework/Timeouts';
const cameraDirectionMap = {
    down: 'Down',
    in: 'In',
    left: 'Left',
    out: 'Out',
    right: 'Right',
    up: 'Up',
};
function isZoomCameraConfig(config) {
    return typeof config === 'object' && 'zoomId' in config && typeof config.zoomId === 'string';
}
const jsonRegExp = /^{[\s\S]*?[\r\n]+}/;
const terminators = ['\r', '\n'];
const callExtras = {
    voip: { canDial: true },
    zoom: { canEndMeeting: false, canInvite: true, sendingContent: false, sendingVideo: false },
};
export class Zoom {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.framework = framework;
        // other class properties and constructor
        this.allLayouts = {};
        this.audioInputs = [];
        this.audioOutputs = [];
        this.bookingsUpdated = false;
        this.callInListEntries = [];
        this.callInListKey = 'callInListKey';
        this.callerJids = {};
        this.callStatus = 'NOT_IN_MEETING';
        this.cameraSharingInfo = {};
        this.changingContentSource = false;
        this.config = {
            equipmentId: '',
            equipmentType: '-LDmH6a8aVJTYIXyiiQ9',
            ipAddress: '',
        };
        this.displayDialString = '';
        this.hdmiDeviceInfo = [
            { vid: 0x046d, pid: 0x086c },
            { vid: 0x2935, pid: 0x0006 }, // Magwell
        ];
        this.isAirHostEnabled = false;
        this.isRecordingConsentRequired = false;
        this.jidReplaceRegex = /[^a-zA-Z0-9-_]/g;
        this.layoutIds = {
            gallery: Sutl.generateFirebaseId(),
            shareAll: Sutl.generateFirebaseId(),
            speaker: Sutl.generateFirebaseId(),
            strip: Sutl.generateFirebaseId(),
        };
        this.meetingType = 'NONE';
        this.numberOfScreens = 1;
        this.pollIndex = 0;
        this.presenceMap = {
            PRESENCE_AWAY: 'away',
            PRESENCE_BUSY: 'busy',
            PRESENCE_DND: 'dnd',
            PRESENCE_OFFLINE: 'offline',
            PRESENCE_ONLINE: 'online',
            UNKNOWN: 'unknown',
        };
        this.sharingInfo = {};
        this.timeouts = new Timeouts();
        this.videoInputIds = {};
        this.voipPrimaryKey = 'voip0';
        this.zoomCameras = [];
        this.zoomPrimaryKey = 'zoom0';
        this.zoomSecondaryKey = 'zoom1';
        // controls
        this.calendar = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const cmd = 'zCommand Bookings List';
                const resp = yield this.send(cmd, 'zEvent', 'BookingsListResult', tags, true);
                return this.parseBookingsListResult(resp);
            }),
            hasControl: (config) => __awaiter(this, void 0, void 0, function* () {
                var _a, _b;
                return ((_a = config.useCalendar) !== null && _a !== void 0 ? _a : false) && !((_b = config.synchronizedCalls) === null || _b === void 0 ? void 0 : _b.zoom);
            }),
            asyncHandlers: [
                {
                    name: 'bookings',
                    match: (resp) => this.match(resp, 'zEvent', 'BookingsListResult'),
                    callback: (resp) => {
                        this.parseBookingsListResult(resp);
                    },
                },
                {
                    name: 'bookingsUpdated',
                    match: (resp) => this.match(resp, 'zEvent', 'Bookings Updated'),
                    callback: (resp) => {
                        this.bookingsUpdated = true;
                    },
                },
            ],
        };
        this.callInList = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                if (key !== this.callInListKey)
                    throw new Error(`invalid callInList key: ${key}`);
                return this.callInListEntries;
            }),
        };
        this.cameras = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.makeCameras();
            }),
        };
        this.cameraMove = {
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _c, _d;
                if (!this.state.calls.isInCall('zoom')) {
                    this.log.warning(`can't move camera: not in a Zoom video call`);
                    return value;
                }
                if (value.action === 'start') {
                    let needToStop = false;
                    if (this.cameraMoveTimeout.isRunning) {
                        this.log.error('cameraMove: received start while move timeout active');
                        this.cameraMoveTimeout.clear();
                        needToStop = true;
                    }
                    if (this.cameraContinueTimeout.isRunning) {
                        this.log.error('cameraMove: received start while continue timeout active');
                        this.cameraContinueTimeout.clear();
                        needToStop = true;
                    }
                    this.cameraMoveDirection = cameraDirectionMap[value.direction];
                    if (needToStop && this.cameraMoveDirection !== undefined) {
                        yield this.setCameraControl({ state: 'Stop', action: this.cameraMoveDirection }, tags);
                    }
                    // Flip left/right if necessary.
                    const selectedCamera = this.findSelectedChannel(this.zoomCameras);
                    if (selectedCamera !== undefined) {
                        const cameraConfig = Object.values((_c = this.config.cameras) !== null && _c !== void 0 ? _c : {}).find(camera => {
                            return 'zoomId' in camera && camera.zoomId === selectedCamera.zoomId;
                        });
                        if (cameraConfig !== undefined) {
                            if (isZoomCameraConfig(cameraConfig)) {
                                if ((_d = cameraConfig.invertPan) !== null && _d !== void 0 ? _d : false) {
                                    if (this.cameraMoveDirection === 'Left') {
                                        this.cameraMoveDirection = 'Right';
                                    }
                                    else if (this.cameraMoveDirection === 'Right') {
                                        this.cameraMoveDirection = 'Left';
                                    }
                                }
                            }
                            else {
                                this.log.error(`invalid camera config: ${JSON.stringify(cameraConfig)}`);
                            }
                        }
                    }
                    yield this.setCameraControl({ state: 'Start', action: this.cameraMoveDirection }, tags);
                    this.cameraMoveTimeout.set(this.onCameraMoveTimeout, 1000);
                    this.cameraContinueTimeout.set(this.onCameraContinueTimeout, 35);
                }
                else if (value.action === 'stop') {
                    if (this.cameraMoveTimeout.isRunning) {
                        this.cameraMoveTimeout.clear();
                    }
                    else {
                        this.log.error(`cameraMove: cameraMoveTimeout is not active`);
                    }
                    if (this.cameraContinueTimeout.isRunning) {
                        this.cameraContinueTimeout.clear();
                    }
                    else {
                        this.log.error(`cameraMove: cameraContinueTimeout is not active`);
                    }
                    this.socket.removeCommands('cameraContinue');
                    if (this.cameraMoveDirection !== undefined) {
                        yield this.setCameraControl({ state: 'Stop', action: this.cameraMoveDirection }, tags);
                        this.cameraMoveDirection = undefined;
                    }
                    else {
                        this.log.error(`cameraMove: direction is ${this.cameraMoveDirection} not undefined`);
                    }
                }
                else if (value.action === 'repeat') {
                    this.cameraMoveTimeout.set(this.onCameraMoveTimeout, 1000);
                }
                return value;
            }),
        };
        this.cameraSelected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                // The framework guarantees that the detected control gets queried first, so we are
                // safe to find the selected camera in this.zoomCameras.
                var _e;
                return (_e = this.findSelectedChannel(this.zoomCameras)) === null || _e === void 0 ? void 0 : _e.zoomId;
            }),
            set: (value, tags) => __awaiter(this, void 0, void 0, function* () {
                if (value === undefined)
                    return value;
                const id = yield this.setVideoCameraSelected(value, tags);
                return id !== null && id !== void 0 ? id : this.state.cameraSelected.value;
            }),
            // Video Camera Line -> selectedCamera async messages handled in detected async handler.
            asyncHandlers: [
                {
                    name: 'cameraSelected',
                    match: (resp) => this.match(resp, 'zConfiguration', 'Video', 'Video.Camera'),
                    callback: (resp) => __awaiter(this, void 0, void 0, function* () {
                        var _f, _g;
                        const selectedId = (_g = (_f = resp.Video) === null || _f === void 0 ? void 0 : _f.Camera) === null || _g === void 0 ? void 0 : _g.selectedId;
                        if (selectedId === undefined)
                            return;
                        // If a new device is plugged in and becomes the camera, the ZR doesn't send a
                        // Video Camera Line status message with the new cameras, so we don't have it
                        // as part of our known cameras. If this happens, we just requery the cameras
                        // and let the cameras async handler update the cameras and the selected camera.
                        if (this.findDriverChannel(this.zoomCameras, selectedId) === undefined) {
                            yield this.socket.sendAsync('zStatus Video Camera Line', 'get.cameras');
                            return;
                        }
                        this.state.cameraSelected.update(selectedId);
                    }),
                },
            ],
        };
        this.cameraSelfView = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _h, _j;
                const cmd = `zConfiguration Video hide_conf_self_video`;
                const resp = (yield this.send(cmd, 'zConfiguration', 'Video', tags, true));
                return !((_j = (_h = resp === null || resp === void 0 ? void 0 : resp.Video) === null || _h === void 0 ? void 0 : _h.hide_conf_self_video) !== null && _j !== void 0 ? _j : false);
            }),
            set: (value, tags) => __awaiter(this, void 0, void 0, function* () {
                const cmd = `zConfiguration Video hide_conf_self_video: ${value ? 'off' : 'on'}`;
                yield this.send(cmd, 'zConfiguration', 'Configuration', tags, true);
                return value;
            }),
            asyncHandlers: [
                {
                    name: 'hideSelfVideo',
                    match: (resp) => this.match(resp, 'zConfiguration', 'Video', 'Video.hide_conf_self_video'),
                    callback: (resp) => __awaiter(this, void 0, void 0, function* () {
                        const value = !resp.Video.hide_conf_self_video;
                        this.state.cameraSelfView.update(value);
                    }),
                },
            ],
        };
        this.cameraSelfViewAvailable = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.computeSelfViewAvailable(this.state.calls.values);
            }),
        };
        this.contentSource = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                // The contentSource is already computed by the detected.get method (which the framework
                // guarantees will be queried first), so we can just return the computed value. Also,
                // the sharingInfo asyncHandler is handled by the detected control.
                return this.state.contentSource.value;
            }),
            set: (value, tags) => __awaiter(this, void 0, void 0, function* () {
                // The ZR can send intermediate ZStatus Sharing messages while we are changing the
                // source. To prevent spurious changes, we don't process the ZStatus Sharing messages
                // while we're changing the source. Then, we (asynchronously) send a query when we're
                // done, just in case we didn't end up in the state we expected.
                this.changingContentSource = true;
                try {
                    if (this.callStatus === 'IN_MEETING') {
                        if (value === 'airPlay' || value === 'laptop') {
                            yield this.setSharingInstructions(value, tags);
                        }
                        else if (value === 'hdmi') {
                            yield this.setHdmiSharing(true, tags);
                        }
                        else if (value.startsWith('camera.')) {
                            if (this.meetingType !== 'SHARING_LAPTOP') {
                                const id = value.slice(7);
                                yield this.setCallShareCamera(id, true, tags);
                            }
                        }
                        else if (value === 'videoConference') {
                            if (this.meetingType === 'SHARING_LAPTOP') {
                                yield this.setDisconnectSharingCall(tags);
                                //await this.setSharingInstructions(value, tags);
                            }
                            else {
                                if (this.state.contentSource.value === 'hdmi') {
                                    yield this.setHdmiSharing(false, tags);
                                }
                                else if (this.state.contentSource.value === 'airPlay' ||
                                    this.state.contentSource.value === 'laptop') {
                                    yield this.setSharingDisconnect(tags);
                                }
                                else if (this.state.contentSource.value.startsWith('camera.')) {
                                    const id = this.state.contentSource.value.slice(7);
                                    yield this.setCallShareCamera(id, false, tags);
                                }
                                yield this.setSharingInstructions(value, tags);
                            }
                        }
                    }
                    else if (this.callStatus === 'NOT_IN_MEETING') {
                        if (value !== 'videoConference' && !value.startsWith('camera.')) {
                            yield this.setDialSharing(value, tags);
                        }
                    }
                }
                catch (err) {
                    this.log.warning(`contentSource.set: error: ` + err);
                }
                this.changingContentSource = false;
                // The ZR can report incorrect intermediate sharing values, so instead of querying
                // immediately, we wait for a short time to let Zoom's values settle out.
                this.sharingQueryTimeout.set(() => __awaiter(this, void 0, void 0, function* () {
                    yield this.querySharing();
                }), 1000);
                return value;
            }),
        };
        this.contentState = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                // The contentState is already computed by the detected.get method (which the framework
                // guarantees will be queried first), so we can just return the computed value. Also,
                // the sharingInfo asyncHandler is handled by the detected control.
                return this.state.contentState.values[key];
            }),
        };
        this.contentStop = {
            set: (value, tags) => __awaiter(this, void 0, void 0, function* () {
                if (value === 'hdmi') {
                    yield this.setHdmiSharing(false, tags);
                }
                else if (value === 'airPlay' || value === 'laptop') {
                    if (this.callStatus !== 'IN_MEETING') {
                        this.log.warning('no API command to stop sharing when not in a call');
                    }
                    else {
                        yield this.setSharingDisconnect(tags);
                    }
                }
                else if (value.startsWith('camera.')) {
                    const id = value.slice(7);
                    yield this.setCallShareCamera(id, false, tags);
                }
                else {
                    this.log.error(`contentStop: invalid code: ${value}`);
                }
                if (this.meetingType === 'SHARING_LAPTOP') {
                    yield this.setDisconnectSharingCall(tags);
                }
                return value;
            }),
        };
        // In addition to the detected channels, we handle the following other objects in the detected
        // control. We handle them here in order to have them in one place and also because we need
        // them to compute other controls, and the framework guarantees the detected control gets
        // queried first.
        //
        // - capabilities
        // - numberOfScreens
        // - selectedCamera, selectedMicrophone, selectedSpeaker
        // - sharingInfo
        // - videoInputs
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                this.handleCapabilities(yield this.getCapabilities(tags));
                this.handleNumberOfScreens(yield this.getNumberOfScreens(tags));
                this.audioInputs = yield this.getAudioInputs(tags);
                this.audioOutputs = yield this.getAudioOutputs(tags);
                this.zoomCameras = yield this.getCameras(tags);
                this.state.videoInputs.update(this.makeVideoInputs());
                yield this.querySharing();
                return this.makeDetected();
            }),
            asyncHandlers: [
                {
                    name: 'audioInputs',
                    match: (resp) => this.match(resp, 'zStatus', 'Audio Input Line'),
                    callback: (resp) => {
                        this.audioInputs = this.parseAudioInputs(resp);
                        this.state.detected.update(this.makeDetected());
                        const selected = this.findSelectedChannel(this.audioInputs);
                        const info = Object.assign({}, this.state.equipmentInfo.value);
                        if (selected === undefined) {
                            delete info.selectedMicrophone;
                        }
                        else {
                            info.selectedMicrophone = selected.zoomId;
                        }
                        this.state.equipmentInfo.update(info);
                    },
                },
                {
                    name: 'audioOutputs',
                    match: (resp) => this.match(resp, 'zStatus', 'Audio Output Line'),
                    callback: (resp) => {
                        this.audioOutputs = this.parseAudioOutputs(resp);
                        this.state.detected.update(this.makeDetected());
                        const selected = this.findSelectedChannel(this.audioOutputs);
                        const info = Object.assign({}, this.state.equipmentInfo.value);
                        if (selected === undefined) {
                            delete info.selectedSpeaker;
                        }
                        else {
                            info.selectedSpeaker = selected.zoomId;
                        }
                        this.state.equipmentInfo.update(info);
                    },
                },
                {
                    name: 'cameras',
                    match: (resp) => this.match(resp, 'zStatus', 'Video Camera Line'),
                    callback: (resp) => {
                        this.zoomCameras = this.parseCameras(resp);
                        this.state.detected.update(this.makeDetected());
                        this.state.cameras.update(this.makeCameras());
                        const selectedCamera = this.findSelectedChannel(this.zoomCameras);
                        this.state.cameraSelected.update(selectedCamera === null || selectedCamera === void 0 ? void 0 : selectedCamera.zoomId);
                    },
                },
                {
                    name: 'capabilities',
                    match: (resp) => this.match(resp, 'zStatus', 'Capabilities'),
                    callback: (resp) => {
                        this.handleCapabilities(resp);
                    },
                },
                {
                    name: 'numberOfScreens',
                    match: (resp) => this.match(resp, 'zStatus', 'NumberOfScreens'),
                    callback: (resp) => {
                        this.handleNumberOfScreens(resp);
                    },
                },
                {
                    name: 'cameraShare',
                    match: (resp) => this.match(resp, 'zStatus', 'CameraShare'),
                    callback: (resp) => {
                        // Don't process sharing status messages after we've just changed the content
                        // source and are waiting to re-query the sharing instructions. The ZR can
                        // report incorrect intermediate values, so we wait for them to settle out.
                        if (this.sharingQueryTimeout.isRunning)
                            return;
                        this.handleCameraShare(resp);
                    },
                },
                {
                    name: 'recordingConsent',
                    match: (resp) => this.match(resp, 'zEvent', 'RecordingConsent'),
                    callback: (resp) => {
                        var _a, _b, _c, _d;
                        const call = this.state.calls.values[this.zoomPrimaryKey];
                        if (typeof ((_a = resp === null || resp === void 0 ? void 0 : resp.RecordingConsent) === null || _a === void 0 ? void 0 : _a.isShow) === 'string') {
                            this.isRecordingConsentRequired = ((_b = resp === null || resp === void 0 ? void 0 : resp.RecordingConsent) === null || _b === void 0 ? void 0 : _b.isShow) === 'true';
                        }
                        else {
                            this.isRecordingConsentRequired = (_d = (_c = resp === null || resp === void 0 ? void 0 : resp.RecordingConsent) === null || _c === void 0 ? void 0 : _c.isShow) !== null && _d !== void 0 ? _d : false;
                        }
                        if ((call.callState === 'connected' || call.callState === 'ringing') &&
                            (call.callExtendedState === undefined ||
                                call.callExtendedState === 'recordingConsentRequired')) {
                            if (call.callExtendedState === 'recordingConsentRequired' &&
                                !this.isRecordingConsentRequired) {
                                this.state.calls.updateCall(this.zoomPrimaryKey, {
                                    callExtendedState: null,
                                });
                            }
                            else if (call.callExtendedState === undefined &&
                                this.isRecordingConsentRequired) {
                                this.state.calls.updateCall(this.zoomPrimaryKey, {
                                    callExtendedState: 'recordingConsentRequired',
                                });
                            }
                        }
                    },
                },
                {
                    name: 'sharingInfo',
                    match: (resp) => this.match(resp, 'zStatus', 'Sharing'),
                    callback: (resp) => {
                        // Don't process sharing status messages after we've just changed the content
                        // source and are waiting to re-query the sharing instructions. The ZR can
                        // report incorrect intermediate values, so we wait for them to settle out.
                        if (this.sharingQueryTimeout.isRunning)
                            return;
                        this.handleSharing(resp);
                    },
                },
            ],
        };
        this.directory = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _k, _l;
                const directory = {};
                const limit = 100;
                let offset = 0;
                let done = false;
                while (!done) {
                    const resp = (yield this.send(`zCommand Phonebook List Offset: ${offset} Limit: ${limit}`, 'zCommand', 'PhonebookListResult', tags, true));
                    const contacts = (_l = (_k = resp.PhonebookListResult) === null || _k === void 0 ? void 0 : _k.Contacts) !== null && _l !== void 0 ? _l : [];
                    for (const contact of contacts) {
                        const entryResult = this.parseContact(contact);
                        if (entryResult === undefined)
                            continue;
                        const [key, entry] = entryResult;
                        directory[key] = entry;
                    }
                    offset += contacts.length;
                    done = contacts.length !== limit;
                }
                yield this.setPhonebookSubscribe();
                return directory;
            }),
            hasControl: (config) => __awaiter(this, void 0, void 0, function* () {
                var _m, _o;
                return ((_m = config.useDirectory) !== null && _m !== void 0 ? _m : false) && !((_o = config.synchronizedCalls) === null || _o === void 0 ? void 0 : _o.zoom);
            }),
            asyncHandlers: [
                {
                    name: 'updatedContact',
                    match: (resp) => this.match(resp, 'zEvent', 'Phonebook'),
                    callback: (resp) => {
                        var _a, _b, _c, _d;
                        const contact = (_d = (_b = (_a = resp.Phonebook) === null || _a === void 0 ? void 0 : _a['Updated Contact']) !== null && _b !== void 0 ? _b : (_c = resp.Phonebook) === null || _c === void 0 ? void 0 : _c['Added Contact']) !== null && _d !== void 0 ? _d : {};
                        const entryResult = this.parseContact(contact);
                        if (entryResult === undefined)
                            return;
                        const [key, entry] = entryResult;
                        this.state.directory.update(key, entry);
                    },
                },
            ],
        };
        this.equipmentInfo = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const audioInputVolume = yield this.getAudioInputVolume(tags);
                const audioOutputVolume = yield this.getAudioOutputVolume(tags);
                // The framework guarantees that the detected control gets queried first, so we are
                // safe to find the selected camera, microphone, and speaker values in this.zoomCameras,
                // this.audioInputs and this.audioOutputs.
                const selectedCamera = this.findSelectedChannel(this.zoomCameras);
                const selectedInput = this.findSelectedChannel(this.audioInputs);
                const selectedOutput = this.findSelectedChannel(this.audioOutputs);
                const info = {};
                if (audioInputVolume !== undefined)
                    info.microphoneVolume = audioInputVolume;
                if (audioOutputVolume !== undefined)
                    info.speakerVolume = audioOutputVolume;
                if (selectedCamera !== undefined)
                    info.selectedCamera = selectedCamera.zoomId;
                if (selectedInput !== undefined)
                    info.selectedMicrophone = selectedInput.zoomId;
                if (selectedOutput !== undefined)
                    info.selectedSpeaker = selectedOutput.zoomId;
                return info;
            }),
            asyncHandlers: [
                {
                    name: 'audioInput',
                    match: (resp) => this.match(resp, 'zConfiguration', 'Audio'),
                    callback: (resp) => {
                        var _a, _b, _c, _d, _e, _f, _g, _h;
                        const info = Object.assign({}, this.state.equipmentInfo.value);
                        if (((_b = (_a = resp.Audio) === null || _a === void 0 ? void 0 : _a.Input) === null || _b === void 0 ? void 0 : _b.volume) !== undefined) {
                            info.microphoneVolume = resp.Audio.Input.volume;
                        }
                        if (((_d = (_c = resp.Audio) === null || _c === void 0 ? void 0 : _c.Input) === null || _d === void 0 ? void 0 : _d.selectedId) !== undefined) {
                            info.selectedMicrophone = resp.Audio.Input.selectedId;
                        }
                        if (((_f = (_e = resp.Audio) === null || _e === void 0 ? void 0 : _e.Output) === null || _f === void 0 ? void 0 : _f.selectedId) !== undefined) {
                            info.selectedSpeaker = resp.Audio.Output.selectedId;
                        }
                        if (((_h = (_g = resp.Audio) === null || _g === void 0 ? void 0 : _g.Output) === null || _h === void 0 ? void 0 : _h.volume) !== undefined) {
                            info.speakerVolume = resp.Audio.Output.volume;
                            this.state.volume.update(resp.Audio.Output.volume);
                        }
                        this.state.equipmentInfo.update(info);
                    },
                },
                // cameras, cameraSelected:
                // zConfiguration Video async messages handled in cameraSelected async handler.
                // zStatus Video Camera Line async messages handled in detected async handler.
                // selectedSpeaker:
                // zStatus Audio Output Line async messages handled in detected async handler.
                // selectedMicrophone:
                // zStatus AudioInput Line async messages handled in detected async handler.
            ],
        };
        this.equipmentSettings = {
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                if (key === 'microphoneVolume') {
                    if (typeof value !== 'number') {
                        throw new Error('microphoneVolume value not a number');
                    }
                    const volume = yield this.setAudioInputVolume(value, tags);
                    if (volume !== undefined) {
                        const info = Object.assign(Object.assign({}, this.state.equipmentInfo.value), { microphoneVolume: volume });
                        this.state.equipmentInfo.update(info);
                    }
                }
                else if (key === 'selectCamera') {
                    if (typeof value !== 'string') {
                        throw new Error('selectCamera value not a string');
                    }
                    const id = yield this.setVideoCameraSelected(value, tags);
                    if (id !== undefined) {
                        const info = Object.assign(Object.assign({}, this.state.equipmentInfo.value), { selectedCamera: id });
                        this.state.equipmentInfo.update(info);
                        this.state.cameraSelected.update(id);
                    }
                }
                else if (key === 'selectMicrophone') {
                    if (typeof value !== 'string') {
                        throw new Error('selectMicrophone value not a string');
                    }
                    const id = yield this.setAudioInputSelected(value, tags);
                    if (id !== undefined) {
                        const info = Object.assign(Object.assign({}, this.state.equipmentInfo.value), { selectedMicrophone: id });
                        this.state.equipmentInfo.update(info);
                    }
                }
                else if (key === 'selectSpeaker') {
                    if (typeof value !== 'string') {
                        throw new Error('selectSpeaker value not a string');
                    }
                    const id = yield this.setAudioOutputSelected(value, tags);
                    if (id !== undefined) {
                        const info = Object.assign(Object.assign({}, this.state.equipmentInfo.value), { selectedSpeaker: id });
                        this.state.equipmentInfo.update(info);
                    }
                }
                else if (key === 'speakerVolume') {
                    if (typeof value !== 'number') {
                        throw new Error('speakerVolume not a number');
                    }
                    const volume = yield this.setAudioOutputVolume(value);
                    if (volume !== undefined) {
                        const info = Object.assign(Object.assign({}, this.state.equipmentInfo.value), { speakerVolume: volume });
                        this.state.equipmentInfo.update(info);
                        this.state.volume.update(volume);
                    }
                }
                else {
                    throw new Error(`unsupported setting: ${key}`);
                }
                return value;
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _p, _q;
                const cmd = 'zStatus SystemUnit';
                const type = 'zStatus';
                const topKey = 'SystemUnit';
                const resp = (yield this.send(cmd, type, topKey, tags, true));
                return (_q = (_p = resp.SystemUnit) === null || _p === void 0 ? void 0 : _p.room_version) !== null && _q !== void 0 ? _q : 'Unknown';
            }),
        };
        this.mute = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _r, _s, _t, _u;
                let mute;
                if (this.state.calls.isInCall('voip')) {
                    const resp = (yield this.send('zConfiguration SIPCall Microphone Mute', 'zConfiguration', 'SIPCall', tags, true));
                    mute = (_s = (_r = resp.SIPCall) === null || _r === void 0 ? void 0 : _r.Microphone) === null || _s === void 0 ? void 0 : _s.Mute;
                }
                else {
                    const resp = (yield this.send('zConfiguration Call Microphone Mute', 'zConfiguration', 'Call', tags, true));
                    mute = (_u = (_t = resp.Call) === null || _t === void 0 ? void 0 : _t.Microphone) === null || _u === void 0 ? void 0 : _u.Mute;
                }
                return mute !== null && mute !== void 0 ? mute : this.state.mute.value;
            }),
            set: (value, tags) => __awaiter(this, void 0, void 0, function* () {
                let cmd;
                if (this.state.calls.isInCall('voip')) {
                    cmd = `zConfiguration SIPCall Microphone Mute: ${value ? 'on' : 'off'}`;
                }
                else {
                    if (this.callStatus === 'NOT_IN_MEETING') {
                        return value;
                    }
                    else {
                        cmd = `zConfiguration Call Microphone Mute: ${value ? 'on' : 'off'}`;
                    }
                }
                const prev = this.state.mute.value;
                this.state.mute.update(value);
                const resp = (yield this.socket.send(cmd, (resp) => {
                    if (this.match(resp, 'zConfiguration', 'Configuration', [], true, false) ||
                        this.match(resp, 'zCommand', 'CommonResult', [], true, false)) {
                        return resp;
                    }
                    return undefined;
                }, tags));
                // Re-query mute (asynchronously) in case Zoom doesn't allow us to set it (e.g., if
                // the host is preventing us from unmuting as in SNTY-1380).
                this.queryMute();
                return resp.topKey === 'Configuration' ? value : prev;
            }),
            // Mute async handlers (zConfiguration Call) implemented in telephony control.
        };
        this.telephony = {
            answer: (key) => __awaiter(this, void 0, void 0, function* () {
                var _v;
                const call = this.state.calls.values[key];
                const jid = this.callerJids[key];
                if (call === undefined)
                    throw new Error(`invalid callId: ${key}`);
                if (jid === undefined)
                    throw new Error(`no jid for ${key}`);
                if (call.callType === 'zoom') {
                    const resp = (yield this.send(`zCommand Call Accept callerJID: ${jid}`, 'zCommand', 'CallAcceptRejectResult', [], true));
                    if (((_v = resp.Status) === null || _v === void 0 ? void 0 : _v.state) !== 'OK') {
                        this.queryCalls();
                        return false;
                    }
                    else {
                        // We could be in a sharing-only meeting. If so, leave the meeting type as
                        // SHARING_LAPTOP so that when we receive a call status message indicating the
                        // sharing-only meeting has disconnected, we won't mark this incoming call as
                        // disconnected. The ZR will send a call info message to change the meeting
                        // type to NORMAL before it sends the call status message to chagne the call
                        // status to IN_MEETING.
                        if (this.callStatus !== 'IN_MEETING')
                            this.meetingType = 'NORMAL';
                    }
                }
                else if (call.callType === 'voip') {
                    // We received information on an undocumented Zoom API command from Olivier
                    // Peardon at Colortone on September 18, 2019. He got the information from a Zoom
                    // engineer. We think they have resolved the issue with accepting and rejecting
                    // SIP calls, but have not yet officially documented it. Not that this requires
                    // ZR 4.5.0 or later.
                    //
                    // The ZR doesn't send back any response for the Accept SIPCall command. It's
                    // probably a bug on their side, but until they fix it, we'll just not expect a
                    // response.
                    try {
                        this.socket.sendAsync(`zCommand Accept SIPCall CallID: ${jid}`); // don't await
                    }
                    catch (_w) { }
                }
                return true;
            }),
            call: (value) => __awaiter(this, void 0, void 0, function* () {
                var _x, _y;
                if (value.callType === 'zoom') {
                    if (value.dialString === 'none') {
                        const users = Object.values((_x = value.invite) !== null && _x !== void 0 ? _x : {});
                        if (this.meetingType === 'SHARING_LAPTOP' &&
                            this.callStatus !== 'NOT_IN_MEETING') {
                            const csv = Object.values(this.state.contentState.values);
                            const sendingContent = csv.find(s => s === 'sending') !== undefined;
                            // The sharing call is already connected, so we won't get an API
                            // message about it. We connect the call here
                            this.state.calls.connect(this.zoomPrimaryKey, Object.assign(Object.assign({}, callExtras.zoom), { sendingContent, dialString: this.meetingId, displayDialString: this.displayDialString }));
                            yield this.setSharingToNormal();
                            yield this.setCallInvite(users);
                            return undefined;
                        }
                        else {
                            this.state.calls.call(this.zoomPrimaryKey);
                            if (users.length === 0) {
                                yield this.setDialStartPmi();
                            }
                            else {
                                yield this.setInvite(users);
                            }
                            return undefined;
                        }
                    }
                    else {
                        const meetingPassword = (_y = value.meetingCodes) === null || _y === void 0 ? void 0 : _y[0];
                        this.state.calls.call(this.zoomPrimaryKey, {
                            dialString: value.dialString,
                            meetingPassword,
                        });
                        if (this.callStatus === 'IN_MEETING' && this.meetingType === 'SHARING_LAPTOP') {
                            yield this.setDisconnectSharingCall();
                        }
                        yield this.setDialJoin(value.dialString, meetingPassword);
                        return undefined;
                    }
                }
                else if (value.callType === 'voip') {
                    this.state.calls.call(this.voipPrimaryKey, { dialString: value.dialString });
                    yield this.setDialPhoneCallOut(value.dialString);
                    return undefined;
                }
                this.log.error(`unsupported call type: ${value.callType}`);
                return undefined;
            }),
            callLayout: (key, value) => __awaiter(this, void 0, void 0, function* () {
                if (key !== this.zoomPrimaryKey)
                    throw new Error(`callLayout: invalid key = ${key}`);
                const layout = this.allLayouts[value];
                if (layout === undefined)
                    throw new Error(`callLayout: invalid value = ${value}`);
                const cmd = `zConfiguration Call Layout Style: ${layout}`;
                const type = 'zConfiguration';
                const topKey = 'Client';
                const tags = ['set.callLayout'];
                const resp = yield this.send(cmd, type, topKey, tags, undefined);
                return this.parseLayoutStyle(resp);
            }),
            consentToRecording: (key, value) => __awaiter(this, void 0, void 0, function* () {
                const cmd = `zCommand Agree Recording: ${value ? 'on' : 'off'}`;
                yield this.send(cmd, 'zCommand', 'AgreeRecordingResult', [], true);
                if (value) {
                    this.state.calls.updateCall(this.zoomPrimaryKey, { callExtendedState: null });
                }
                else {
                    this.state.calls.end(this.zoomPrimaryKey);
                    yield this.setCallDisconnect(false);
                }
                return value;
            }),
            dial: (key, value) => __awaiter(this, void 0, void 0, function* () {
                const call = this.state.calls.values[key];
                const jid = this.callerJids[key];
                if (call === undefined)
                    throw new Error(`invalid callId: ${key}`);
                if (call.callType !== 'voip') {
                    throw new Error(`dial not supported on ${call.callType} calls`);
                }
                if (jid === undefined)
                    throw new Error(`no jid for ${key}`);
                // The ZR doesn't send back any response for the SendSIPDTMF command. I'm pretty sure
                // that's a bug on their side, but until they fix it we'll just not expect a response.
                for (const digit of value) {
                    try {
                        this.socket.sendAsync(`zCommand SendSIPDTMF callId: ${jid} key: ${digit}`);
                    }
                    catch (_z) { }
                }
                return value;
            }),
            getCalls: () => __awaiter(this, void 0, void 0, function* () {
                yield this.queryCalls(true, true);
                this.state.cameraSelfViewAvailable.update(this.computeSelfViewAvailable(this.state.calls.values));
                return Object.assign({}, this.state.calls.values);
            }),
            getDisabledCalls: () => __awaiter(this, void 0, void 0, function* () {
                return this.makeDisabledCalls();
            }),
            getMaxCalls: () => __awaiter(this, void 0, void 0, function* () {
                return { voip: this.config.audioDialingEnabled ? 1 : 0, zoom: 1 };
            }),
            getSupportsInvitation: () => __awaiter(this, void 0, void 0, function* () {
                return { zoom: true };
            }),
            hangup: (key, endMeeting) => __awaiter(this, void 0, void 0, function* () {
                const call = this.state.calls.values[key];
                if (call === undefined)
                    throw new Error(`invalid callId: ${key}`);
                // Sometimes the polls need a little extra time for their state to catch up with the
                // actual state of the system. For example, see SNTY-3020. We suspend polling for a bit
                // so that we don't get a stale state which could cause a glitch or error.
                this.framework.suspendPolling(3000);
                if (call.callType === 'voip') {
                    const jid = this.callerJids[key];
                    if (jid === undefined)
                        throw new Error(`no jid for ${key}`);
                    this.state.calls.end(key);
                    yield this.setDialPhoneHangup(jid);
                    return false;
                }
                else if (call.callType === 'zoom') {
                    if (this.isCallWaitingForPassword(call)) {
                        return true;
                    }
                    else {
                        this.state.calls.end(key);
                        yield this.setCallDisconnect(endMeeting === true);
                        return false;
                    }
                }
                throw new Error(`unexpected callType ${call.callType} for ${key} call`);
            }),
            inviteToCall: (key, invitees) => __awaiter(this, void 0, void 0, function* () {
                const call = this.state.calls.values[key];
                if (call === undefined)
                    throw new Error(`invalid callId: ${key}`);
                const users = Object.values(invitees);
                const zoomUsers = users.filter(u => u.type === 'zoom').map(u => u.address);
                const h323Users = users.filter(u => u.type === 'h323');
                const sipUsers = users.filter(u => u.type === 'sip');
                yield Promise.all([
                    this.setCallInvite(zoomUsers),
                    this.setCallInviteH323(h323Users),
                    this.setCallInviteSip(sipUsers),
                ]);
            }),
            muteParticipant: (key, participantId, value) => __awaiter(this, void 0, void 0, function* () {
                const mute = value ? 'on' : 'off';
                if (participantId === '*') {
                    const cmd = `zCommand Call MuteAll Mute: ${mute}`;
                    yield this.send(cmd, 'zCommand', 'CallMuteAllResult', [], true);
                }
                else {
                    const p = this.state.callParticipants.getParticipant(key, participantId);
                    if (p === undefined)
                        throw new Error(`invalid participantId: ${participantId}`);
                    if (p.userId === undefined) {
                        throw new Error(`no userId for participantId ${participantId}`);
                    }
                    const cmd = `zCommand Call MuteParticipant Mute: ${mute} Id: ${p.userId}`;
                    yield this.send(cmd, 'zCommand', 'CallMuteParticipantResult', [], true);
                }
                this.queryMute();
                return value;
            }),
            muteParticipantVideo: (key, participantId, value) => __awaiter(this, void 0, void 0, function* () {
                const p = this.state.callParticipants.getParticipant(key, participantId);
                if (p === undefined)
                    throw new Error(`invalid participantId: ${participantId}`);
                if (p.userId === undefined) {
                    throw new Error(`no userId for participantId ${participantId}`);
                }
                const mute = value ? 'on' : 'off';
                const cmd = `zCommand Call MuteParticipantVideo Mute: ${mute} Id: ${p.userId}`;
                yield this.send(cmd, 'zCommand', 'CallMuteParticipantVideoResult', [], true);
                return value;
            }),
            reject: (key) => __awaiter(this, void 0, void 0, function* () {
                const call = this.state.calls.values[key];
                const jid = this.callerJids[key];
                if (call === undefined)
                    throw new Error(`invalid callId: ${key}`);
                if (jid === undefined)
                    throw new Error(`no jid for ${key}`);
                yield this.setCallReject(call.callType, jid);
                return true;
            }),
            sendHostKey: (key, code) => __awaiter(this, void 0, void 0, function* () {
                const call = this.state.calls.values[key];
                if (call === undefined)
                    throw new Error(`invalid callId: ${key}`);
                if (call.dialString === undefined)
                    throw new Error(`no dial string for call ${key}`);
                yield this.setCallHostClaim(code, ['sendHostKey']);
            }),
            sendMeetingCode: (key, code) => __awaiter(this, void 0, void 0, function* () {
                const call = this.state.calls.values[key];
                if (call === undefined)
                    throw new Error(`invalid callId: ${key}`);
                if (call.dialString === undefined)
                    throw new Error(`no dial string for call ${key}`);
                if (!this.isCallWaitingForPassword(call)) {
                    this.log.warning('not waiting for password');
                    return;
                }
                this.state.calls.updateCall(key, {
                    callExtendedState: 'passwordSubmitted',
                    meetingPassword: code,
                });
                yield this.setDialJoin(call.dialString, code);
            }),
            sendVideo: (key, value) => __awaiter(this, void 0, void 0, function* () {
                const cmd = `zConfiguration Call Camera Mute: ${value ? 'off' : 'on'}`;
                yield this.send(cmd, 'zConfiguration', 'Configuration', [], true);
                return value;
            }),
            spotlightParticipant: (key, participantId, value) => __awaiter(this, void 0, void 0, function* () {
                const enable = value ? 'on' : 'off';
                const p = this.state.callParticipants.getParticipant(key, participantId);
                if (p === undefined)
                    throw new Error(`invalid participantId: ${participantId}`);
                if (p.userId === undefined) {
                    throw new Error(`no userId for participantId ${participantId}`);
                }
                const cmd = `zCommand Call Spotlight Id: ${p.userId} Enable: ${enable}`;
                yield this.send(cmd, 'zCommand', 'SetSpotlight', [], true);
                this.queryMute();
                return value;
            }),
            asyncHandlers: [
                {
                    name: 'callConfiguration',
                    match: (resp) => this.match(resp, 'zConfiguration', 'Call') ||
                        this.match(resp, 'zConfiguration', 'SIPCall'),
                    callback: (resp) => {
                        this.handleZConfigurationCall(resp);
                    },
                },
                {
                    name: 'callConnectError',
                    match: (resp) => this.match(resp, 'zEvent', 'CallConnectError'),
                    callback: (resp) => {
                        var _a;
                        const message = (_a = resp.CallConnectError) === null || _a === void 0 ? void 0 : _a.error_message;
                        const call = this.state.calls.values[this.zoomPrimaryKey];
                        if (call.callState === 'ringing' &&
                            message === 'This meeting ID is not valid. Please check and try again.') {
                            this.state.calls.updateCall(this.zoomPrimaryKey, {
                                callExtendedState: 'invalidDialString',
                            });
                            // Query call status, but don't send query for call info. If we got this
                            // error message, we're probably not in a call. If we're not in a call, the
                            // call info request may not generate a response, which causes the framework
                            // to lag while waiting for the timeout on the response.
                            this.queryCalls(false);
                        }
                    },
                },
                {
                    name: 'callDisconnect',
                    match: (resp) => this.match(resp, 'zEvent', 'CallDisconnect'),
                    callback: (resp) => {
                        const call = this.state.calls.values[this.zoomPrimaryKey];
                        if (call.callState === 'connected') {
                            this.state.calls.disconnect(this.zoomPrimaryKey);
                        }
                        this.queryCalls();
                    },
                },
                {
                    name: 'callInfo',
                    match: (resp) => this.match(resp, 'zCommand', 'InfoResult') ||
                        this.match(resp, 'zCommand', 'CallInfoResult'),
                    callback: (resp) => {
                        this.handleCallInfo(resp);
                    },
                },
                {
                    name: 'callLayout',
                    match: (resp) => this.match(resp, 'zStatus', 'Layout'),
                    callback: (resp) => {
                        this.handleLayout(resp);
                    },
                },
                {
                    name: 'callStatus',
                    match: (resp) => this.match(resp, 'zStatus', 'Call', 'Call.Status'),
                    callback: (resp) => this.handleCallStatus(resp),
                },
                {
                    name: 'incomingCallIndication',
                    match: (resp) => this.match(resp, 'zEvent', 'IncomingCallIndication'),
                    callback: (resp) => {
                        var _a, _b, _c, _d, _e, _f, _g;
                        if (((_a = resp.IncomingCallIndication) === null || _a === void 0 ? void 0 : _a.callerJID) === undefined) {
                            this.log.error('missing callerJid on incoming call');
                            return;
                        }
                        const primaryZoomCall = this.state.calls.values[this.zoomPrimaryKey];
                        const primaryVoipCall = this.state.calls.values[this.voipPrimaryKey];
                        const key = primaryZoomCall.callState === 'connected'
                            ? this.zoomSecondaryKey
                            : this.zoomPrimaryKey;
                        const callExtendedState = primaryZoomCall.callState === 'connected' ||
                            primaryVoipCall.callState === 'connected'
                            ? 'answerWillEnd'
                            : undefined;
                        this.state.calls.setIncoming(key, {
                            callExtendedState,
                            contactName: (_b = resp.IncomingCallIndication) === null || _b === void 0 ? void 0 : _b.callerName,
                            dialString: (_d = (_c = resp.IncomingCallIndication) === null || _c === void 0 ? void 0 : _c.meetingID) === null || _d === void 0 ? void 0 : _d.toString(),
                            displayDialString: this.formatMeetingId((_f = (_e = resp.IncomingCallIndication) === null || _e === void 0 ? void 0 : _e.meetingID) === null || _f === void 0 ? void 0 : _f.toString(), 10),
                        });
                        this.callerJids[key] = (_g = resp.IncomingCallIndication) === null || _g === void 0 ? void 0 : _g.callerJID;
                    },
                },
                {
                    name: 'treatedIncomingCallIndication',
                    match: (resp) => this.match(resp, 'zEvent', 'TreatedIncomingCallIndication'),
                    callback: (resp) => {
                        var _a;
                        for (const key of [this.zoomPrimaryKey, this.zoomSecondaryKey]) {
                            const call = this.state.calls.values[key];
                            if (call.callState === 'ringing' &&
                                call.direction === 'incoming' &&
                                this.callerJids[key] === ((_a = resp === null || resp === void 0 ? void 0 : resp.TreatedIncomingCallIndication) === null || _a === void 0 ? void 0 : _a.callerJID)) {
                                this.state.calls.disconnect(key);
                            }
                        }
                    },
                },
                {
                    name: 'layoutStyle',
                    match: (resp) => this.match(resp, 'zConfiguration', 'Client', 'Client.Call.Layout.Style'),
                    callback: (resp) => {
                        this.handleCallLayoutStyle(resp);
                    },
                },
                {
                    name: 'listParticipants',
                    match: (resp) => this.match(resp, 'zCommand', 'CallListParticipantsResult') ||
                        this.match(resp, 'zCommand', 'ListParticipantsResult'),
                    callback: (resp) => {
                        this.handleCallListParticipants(resp);
                    },
                },
                {
                    name: 'meetingNeedsPassword',
                    match: (resp) => this.match(resp, 'zEvent', 'MeetingNeedsPassword'),
                    callback: (resp) => {
                        var _a, _b;
                        if ((_a = resp.MeetingNeedsPassword) === null || _a === void 0 ? void 0 : _a.needsPassword) {
                            const callExtendedState = ((_b = resp.MeetingNeedsPassword) === null || _b === void 0 ? void 0 : _b.wrongAndRetry)
                                ? 'passwordError'
                                : 'password';
                            this.state.calls.updateCall(this.zoomPrimaryKey, { callExtendedState });
                        }
                    },
                },
                {
                    name: 'phoneCallStatus',
                    match: (resp) => this.match(resp, 'zEvent', 'PhoneCallStatus'),
                    callback: (resp) => {
                        var _a, _b, _c, _d, _e, _f, _g, _h;
                        if (((_a = resp.PhoneCallStatus) === null || _a === void 0 ? void 0 : _a.callID) === undefined) {
                            this.log.error('missing callId on incoming call');
                            return;
                        }
                        const isIncomingCall = (_c = (_b = resp.PhoneCallStatus) === null || _b === void 0 ? void 0 : _b.isIncomingCall) !== null && _c !== void 0 ? _c : false;
                        const peerDisplayName = (_d = resp.PhoneCallStatus) === null || _d === void 0 ? void 0 : _d.peerDisplayName;
                        const peerNumber = (_e = resp.PhoneCallStatus) === null || _e === void 0 ? void 0 : _e.peerNumber;
                        const dialString = (_f = resp.PhoneCallStatus) === null || _f === void 0 ? void 0 : _f.peerUri;
                        const contactName = peerDisplayName ? peerNumber : peerDisplayName;
                        const status = (_g = resp.PhoneCallStatus) === null || _g === void 0 ? void 0 : _g.status;
                        if (status === 'PhoneCallStatus_Incoming' && isIncomingCall) {
                            this.state.calls.setIncoming(this.voipPrimaryKey, {
                                contactName,
                                dialString,
                            });
                        }
                        else if (status === 'PhoneCallStatus_Ringing') {
                            if (this.state.calls.values[this.voipPrimaryKey].callState === 'ringing') {
                                this.state.calls.updateCall(this.voipPrimaryKey, {
                                    contactName,
                                    dialString,
                                });
                            }
                            else {
                                this.state.calls.call(this.voipPrimaryKey, { contactName, dialString });
                            }
                        }
                        else if (status === 'PhoneCallStatus_Accepted') {
                        }
                        else if (status === 'PhoneCallStatus_InCall') {
                            const props = Object.assign(Object.assign({}, callExtras.voip), { contactName, dialString });
                            this.state.calls.connect(this.voipPrimaryKey, props);
                        }
                        else if (status === 'PhoneCallStatus_CallOutFailed') {
                            this.state.calls.disconnect(this.voipPrimaryKey);
                        }
                        else if (status === 'PhoneCallStatus_Init') {
                            // noop
                        }
                        else if (status === 'PhoneCallStatus_Terminated') {
                            // noop
                        }
                        else {
                            this.log.debug(`phoneCallStatus: unhandled status: ${status}`);
                        }
                        this.callerJids[this.voipPrimaryKey] = (_h = resp.PhoneCallStatus) === null || _h === void 0 ? void 0 : _h.callID;
                    },
                },
                {
                    name: 'phoneCallTerminated',
                    match: (resp) => this.match(resp, 'zEvent', 'PhoneCallTerminated'),
                    callback: (resp) => {
                        var _a;
                        if (((_a = resp.PhoneCallTerminated) === null || _a === void 0 ? void 0 : _a.callID) !== this.callerJids[this.voipPrimaryKey]) {
                            this.log.error(`phoneCallTerminated: callIds don't match`);
                            return;
                        }
                        this.state.calls.disconnect(this.voipPrimaryKey);
                    },
                },
                {
                    // When the hand raised status of a call participant changes, the ZR does not
                    // automatically send us an update for the call participants. However, we have
                    // noticed that we always seem to get a PinStatusOfScreenNotification whenever
                    // someone's hand raise status changes, so we use this to trigger a query of the
                    // call participants.
                    //
                    // Note: when there are huge numbers of participants, this can cause performance
                    // issues. If Zoom ever starts updating the participant's status individually, we
                    // should remove this query.
                    name: 'pinStatusOfScreenNotification',
                    match: (resp) => this.match(resp, 'zEvent', 'PinStatusOfScreenNotification'),
                    callback: (resp) => __awaiter(this, void 0, void 0, function* () { return this.queryParticipants(); }),
                },
                {
                    name: 'spotlightStatusNotification',
                    match: (resp) => this.match(resp, 'zEvent', 'SpotlightStatusNotification'),
                    callback: (resp) => __awaiter(this, void 0, void 0, function* () { return this.handleSpotlightStatus(resp); }),
                },
                {
                    name: 'updateCallRecordInfo',
                    match: (resp) => this.match(resp, 'zEvent', 'UpdateCallRecordInfo'),
                    callback: (resp) => __awaiter(this, void 0, void 0, function* () {
                        var _0, _1;
                        const call = this.state.calls.values[this.zoomPrimaryKey];
                        if (call.callState === 'disconnecting' || call.callState === 'disconnected') {
                            return;
                        }
                        this.state.calls.updateCall(this.zoomPrimaryKey, {
                            isBeingRecorded: (_1 = (_0 = resp.UpdateCallRecordInfo) === null || _0 === void 0 ? void 0 : _0.meetingIsBeingRecorded) !== null && _1 !== void 0 ? _1 : false,
                        });
                    }),
                },
            ],
        };
        this.videoInputs = {
            get: (tags = []) => __awaiter(this, void 0, void 0, function* () {
                return this.makeVideoInputs();
            }),
        };
        this.volume = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _2;
                return (_2 = (yield this.getAudioOutputVolume(tags))) !== null && _2 !== void 0 ? _2 : this.state.volume.value;
            }),
            set: (value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _3;
                const volume = (_3 = (yield this.setAudioOutputVolume(value, tags))) !== null && _3 !== void 0 ? _3 : this.state.volume.value;
                const info = Object.assign(Object.assign({}, this.state.equipmentInfo.value), { speakerVolume: volume });
                this.state.equipmentInfo.update(info);
                return volume;
            }),
            range: { max: 100, min: 0, round: 1 },
            // zConfiguration Audio async messages handled in equipmentInfo async handler.
        };
        // polling
        this.polls = [
            {
                intervalMs: 3000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    if (this.socket.hasCommands())
                        return;
                    if (this.bookingsUpdated) {
                        const tags = ['poll', 'get.calendar'];
                        yield this.socket.sendAsync('zCommand Bookings List', tags);
                    }
                    else {
                        if (++this.pollIndex > 3) {
                            this.pollIndex = 0;
                            yield this.queryCalls(false, false, true);
                        }
                        else {
                            yield this.getAppVersion(['poll', 'get.appVersion']);
                        }
                    }
                }),
            },
        ];
        this.allLayouts[this.layoutIds.gallery] = 'Gallery';
        this.allLayouts[this.layoutIds.shareAll] = 'ShareAll';
        this.allLayouts[this.layoutIds.speaker] = 'Speaker';
        this.allLayouts[this.layoutIds.strip] = 'Strip';
        this.availableLayouts = Object.assign({}, this.allLayouts);
        this.cameraContinueTimeout = this.timeouts.create();
        this.cameraMoveTimeout = this.timeouts.create();
        this.sharingQueryTimeout = this.timeouts.create();
        this.onCameraMoveTimeout = this.onCameraMoveTimeout.bind(this);
        this.onCameraContinueTimeout = this.onCameraContinueTimeout.bind(this);
    }
    // phase callbacks
    onBeforeConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.callsSubscription !== undefined) {
                this.state.calls.unsubscribe(this.callsSubscription);
                this.callsSubscription = undefined;
            }
        });
    }
    onControl() {
        return __awaiter(this, void 0, void 0, function* () {
            this.callsSubscription = this.state.calls.subscribe(this.onCallChanged.bind(this));
        });
    }
    onConfigure(config) {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.callInListEntries.length = 0;
            this.changingContentSource = false;
            this.config = Object.assign({}, config);
            const videoConferenceId = this.findVideoInputId('0', this.config.videoInputs);
            if (videoConferenceId !== undefined) {
                // Convert old video inputs to new.
                this.config.videoInputs = {};
                this.config.videoInputs[videoConferenceId] = {
                    code: 'videoConference',
                    name: this.framework.translate('Zoom Meeting'),
                    icon: 'video_conference',
                };
                if (!this.config.hasOwnProperty('airPlayContentEnabled') &&
                    !this.config.airPlayContentEnabled) {
                    this.config.videoInputs[Sutl.generateFirebaseId()] = {
                        code: 'airPlay',
                        name: this.framework.translate('iPhone/iPad/Mac'),
                        description: this.framework.translate('Using AirPlay'),
                        icon: 'wireless',
                        farContent: true,
                    };
                }
                this.config.videoInputs[Sutl.generateFirebaseId()] = {
                    code: 'laptop',
                    name: this.framework.translate('Laptop'),
                    description: this.framework.translate('Using Zoom App/Browser'),
                    icon: 'laptop',
                    farContent: true,
                };
                this.config.videoInputs[Sutl.generateFirebaseId()] = {
                    code: 'hdmi',
                    name: this.framework.translate('Laptop'),
                    description: this.framework.translate('Using Cable'),
                    icon: 'laptop',
                    farContent: true,
                };
            }
            // Use the video input ids from the config if they are present, otherwise generate our own.
            this.videoInputIds = {
                airPlay: (_a = this.findVideoInputId('airPlay')) !== null && _a !== void 0 ? _a : Sutl.generateFirebaseId(),
                hdmi: (_b = this.findVideoInputId('hdmi')) !== null && _b !== void 0 ? _b : Sutl.generateFirebaseId(),
                laptop: (_c = this.findVideoInputId('laptop')) !== null && _c !== void 0 ? _c : Sutl.generateFirebaseId(),
                videoConference: (_d = this.findVideoInputId('videoConference')) !== null && _d !== void 0 ? _d : Sutl.generateFirebaseId(),
            };
            // Add camera content sources.
            for (const [id, videoInput] of Object.entries((_e = this.config.videoInputs) !== null && _e !== void 0 ? _e : {})) {
                if (videoInput.code.startsWith('camera.')) {
                    this.videoInputIds[videoInput.code] = id;
                }
            }
            // Start with all calls disconnected, then query call status and call info in
            // telephony.getCalls to populate them asynchronously.
            const zoomProps = Object.assign(Object.assign({}, callExtras.zoom), { callType: 'zoom' });
            this.state.calls.update(this.zoomPrimaryKey, this.state.calls.addMissing(Object.assign({}, zoomProps)));
            this.state.calls.update(this.zoomSecondaryKey, this.state.calls.addMissing(Object.assign(Object.assign({}, zoomProps), { layouts: {
                    current: Object.keys(this.availableLayouts).sort()[0],
                    available: Object.assign({}, this.availableLayouts),
                } })));
            const voipProps = Object.assign(Object.assign({}, callExtras.voip), { callType: 'voip' });
            this.state.calls.update(this.voipPrimaryKey, this.state.calls.addMissing(Object.assign({}, voipProps)));
        });
    }
    onDisconnect(prevPhase) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.callsSubscription !== undefined) {
                this.state.calls.unsubscribe(this.callsSubscription);
                this.callsSubscription = undefined;
            }
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.timeouts.delay(1000);
            yield this.socket.sendAsync('echo off');
            yield this.timeouts.delay(1000);
            yield this.socket.sendAsync('format json');
            yield this.timeouts.delay(1000);
            try {
                yield this.getAppVersion();
            }
            catch (err) {
                return 'verifyFailed';
            }
            yield this.setClientName();
            yield this.setClientVersion();
            return 'connected';
        });
    }
    // other class methods
    computeSelfViewAvailable(calls) {
        let isInCall = false;
        for (const call of Object.values(this.state.calls.values)) {
            if (call.callState === 'connected') {
                isInCall = true;
                break;
            }
        }
        return isInCall;
    }
    findDriverChannel(channels, zoomId) {
        return channels.find(channel => channel.zoomId === zoomId);
    }
    findSelectedChannel(channels) {
        return channels.find(channel => {
            if (channel.selected)
                return true;
            return false;
        });
    }
    findVideoInputId(code, videoInputs) {
        var _a;
        if (videoInputs === undefined)
            videoInputs = (_a = this.config.videoInputs) !== null && _a !== void 0 ? _a : {};
        for (const [id, videoInput] of Object.entries(videoInputs)) {
            if (videoInput.code === code)
                return id;
        }
        return undefined;
    }
    formatMeetingId(meetingId, minLength = 6) {
        if (meetingId === undefined)
            return 'Meeting ID: Unknown';
        const idStr = meetingId.length > minLength
            ? [meetingId.substr(0, 3), meetingId.substr(3, 3), meetingId.substr(6)].join('-')
            : meetingId;
        return `Meeting ID: ${idStr}`;
    }
    getAppVersion(tags = []) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = 'zConfiguration Client appVersion';
            const type = 'zConfiguration';
            const topKey = 'Client';
            const resp = (yield this.send(cmd, type, topKey, tags, true));
            return (_b = (_a = resp.Client) === null || _a === void 0 ? void 0 : _a.appVersion) !== null && _b !== void 0 ? _b : 'Unknown';
        });
    }
    getAudioInputs(tags = []) {
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = 'zStatus Audio Input Line';
            const resp = yield this.send(cmd, 'zStatus', 'Audio Input Line', tags, true);
            return this.parseAudioInputs(resp);
        });
    }
    getAudioInputVolume(tags = []) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = 'zConfiguration Audio Input volume';
            const type = 'zConfiguration';
            const topKey = 'Audio';
            const resp = (yield this.send(cmd, type, topKey, tags, true));
            return (_b = (_a = resp.Audio) === null || _a === void 0 ? void 0 : _a.Input) === null || _b === void 0 ? void 0 : _b.volume;
        });
    }
    getAudioOutputs(tags = []) {
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = 'zStatus Audio Output Line';
            const resp = yield this.send(cmd, 'zStatus', 'Audio Output Line', tags, true);
            return this.parseAudioOutputs(resp);
        });
    }
    getAudioOutputVolume(tags = []) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = 'zConfiguration Audio Output Volume';
            const type = 'zConfiguration';
            const topKey = 'Audio';
            const resp = (yield this.send(cmd, type, topKey, tags, true));
            return (_b = (_a = resp.Audio) === null || _a === void 0 ? void 0 : _a.Output) === null || _b === void 0 ? void 0 : _b.volume;
        });
    }
    getCameras(tags = []) {
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = 'zStatus Video Camera Line';
            const resp = yield this.send(cmd, 'zStatus', 'Video Camera Line', tags, true);
            return this.parseCameras(resp);
        });
    }
    getCameraShare(tags = []) {
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = 'zStatus CameraShare';
            return (yield this.send(cmd, 'zStatus', 'CameraShare', tags, true));
        });
    }
    getCapabilities(tags = []) {
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = 'zStatus Capabilities';
            return (yield this.send(cmd, 'zStatus', 'Capabilities', tags, true));
        });
    }
    getNumberOfScreens(tags = []) {
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = 'zStatus NumberOfScreens';
            const type = 'zStatus';
            const topKey = 'NumberOfScreens';
            return (yield this.send(cmd, type, topKey, tags, true));
        });
    }
    getSharing(tags = []) {
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = 'zStatus Sharing';
            return (yield this.send(cmd, 'zStatus', 'Sharing', tags, true));
        });
    }
    handleCallInfo(resp) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        const meetingId = (_a = resp.InfoResult) === null || _a === void 0 ? void 0 : _a.meeting_id;
        const meetingPassword = (_b = resp.InfoResult) === null || _b === void 0 ? void 0 : _b.meeting_password;
        const meetingType = (_d = (_c = resp.InfoResult) === null || _c === void 0 ? void 0 : _c.meeting_type) !== null && _d !== void 0 ? _d : 'NONE';
        const isHost = (_f = (_e = resp.InfoResult) === null || _e === void 0 ? void 0 : _e.am_i_original_host) !== null && _f !== void 0 ? _f : false;
        this.displayDialString = this.formatMeetingId(meetingId);
        if (meetingId !== this.meetingId ||
            meetingPassword !== this.meetingPassword ||
            meetingType !== this.meetingType) {
            this.meetingId = meetingId;
            this.meetingPassword = meetingPassword;
            this.meetingType = meetingType;
            this.state.videoInputs.update(this.makeVideoInputs());
        }
        this.callInListEntries.length = 0;
        if (((_h = (_g = resp.InfoResult) === null || _g === void 0 ? void 0 : _g.Info) === null || _h === void 0 ? void 0 : _h.callin_country_list) !== undefined) {
            resp.InfoResult.Info.callin_country_list.forEach(entry => {
                var _a;
                this.callInListEntries.push({
                    countryId: entry.id,
                    dialString: (_a = entry.number) === null || _a === void 0 ? void 0 : _a.replace(/\s+/g, ''),
                    displayDialString: entry.display_number,
                    name: entry.name,
                });
            });
        }
        const callInList = this.callInListEntries.length > 0 ? this.callInListEntries : undefined;
        const callInListKey = callInList ? this.callInListKey : null;
        if (callInList)
            this.state.callInList.update(this.callInListKey, callInList);
        this.state.calls.updateCall(this.zoomPrimaryKey, {
            callInListId: callInListKey,
            canEndMeeting: isHost,
            dialString: (_j = this.meetingId) !== null && _j !== void 0 ? _j : 'Unknown',
            displayDialString: this.displayDialString,
            meetingPassword: this.meetingPassword,
            participantId: (_l = (_k = resp.InfoResult) === null || _k === void 0 ? void 0 : _k.participant_id) === null || _l === void 0 ? void 0 : _l.toString(),
            defaultCallInCountryId: (_m = resp.InfoResult) === null || _m === void 0 ? void 0 : _m.default_callin_country,
        });
    }
    handleCallLayoutStyle(resp) {
        this.state.calls.updateLayout(this.zoomPrimaryKey, this.parseLayoutStyle(resp));
    }
    handleCallListParticipants(resp) {
        var _a;
        const call = this.state.calls.values[this.zoomPrimaryKey];
        if (call === undefined) {
            throw new Error('zoom call undefined');
        }
        if (resp.ListParticipantsResult === undefined)
            return;
        if (Array.isArray(resp.ListParticipantsResult)) {
            // If we don't have any participants yet, update them in a single operation. Otherwise,
            // update them individually.
            if (Object.keys((_a = this.state.callParticipants.values[this.zoomPrimaryKey]) !== null && _a !== void 0 ? _a : {})
                .length === 0) {
                const newParticipants = {};
                for (const zoomParticipant of resp.ListParticipantsResult) {
                    this.updateParticipant(zoomParticipant, newParticipants);
                }
                this.state.callParticipants.updateParticipants(this.zoomPrimaryKey, Object.keys(newParticipants).length > 0 ? newParticipants : undefined);
            }
            else {
                this.framework.suspend(30000);
                for (const zoomParticipant of resp.ListParticipantsResult) {
                    this.updateParticipant(zoomParticipant);
                }
                this.framework.resume();
            }
        }
        else {
            this.updateParticipant(resp.ListParticipantsResult);
        }
    }
    handleCallStatus(resp) {
        var _a, _b;
        const callStatus = (_a = resp.Call) === null || _a === void 0 ? void 0 : _a.Status;
        const call = this.state.calls.values[this.zoomPrimaryKey];
        const extraDialInfo = this.meetingId === undefined
            ? {}
            : { dialString: this.meetingId, displayDialString: this.displayDialString };
        if (call === undefined)
            throw new Error('zoom call undefined');
        const callAgeMs = Date.now() - call.start;
        switch (callStatus) {
            case 'NOT_IN_MEETING':
                if (call.callType === 'zoom' &&
                    call.callState === 'ringing' &&
                    call.direction === 'incoming' &&
                    call.start > Date.now() - 60000) {
                    // Incoming Zoom calls are handled by the IncomingCallIndication and
                    // TreatedIncomingCallIndication events. We must ignore the callStatus so we
                    // don't incorrectly mark the call as disconnected. However, we also add a
                    // timeout so it is possible to make the call card go away in case Zoom fails
                    // to send the TreatedIncomingCallIndication event. See SNTY-2540.
                    break;
                }
                this.state.callParticipants.updateParticipants(this.zoomPrimaryKey, undefined);
                if (this.meetingType !== 'SHARING_LAPTOP' &&
                    call.callState !== 'disconnected' &&
                    call.callExtendedState !== 'password' &&
                    call.callExtendedState !== 'passwordError' &&
                    call.callExtendedState !== 'passwordSubmitted') {
                    this.state.calls.disconnect(this.zoomPrimaryKey);
                }
                break;
            case 'CONNECTING_MEETING':
                if (this.meetingType === 'NORMAL' &&
                    (call.callState === 'disconnected' ||
                        call.callState === 'disconnecting' ||
                        (call.callState === 'connected' && callAgeMs > 7000))) {
                    if (call.callState === 'disconnecting' || call.callState === 'connected') {
                        this.state.calls.disconnect(this.zoomPrimaryKey);
                    }
                    this.state.calls.setIncoming(this.zoomPrimaryKey, extraDialInfo);
                }
                break;
            case 'IN_MEETING':
                if (this.meetingType === 'NORMAL' && call.callState !== 'connected') {
                    const csv = Object.values(this.state.contentState.values);
                    const sendingContent = csv.find(s => s === 'sending') !== undefined;
                    // If the call was previously ringing, then don't add the default extras since
                    // they have already been populated via ZRCSAPI messages.
                    const extras = call.callState === 'ringing' ? {} : (_b = callExtras === null || callExtras === void 0 ? void 0 : callExtras.zoom) !== null && _b !== void 0 ? _b : {};
                    this.state.calls.connect(this.zoomPrimaryKey, Object.assign(Object.assign(Object.assign({}, extras), { sendingContent }), extraDialInfo));
                }
                break;
            default:
                this.log.error(`unknown callStatus: ${callStatus}`);
                break;
        }
        if (callStatus !== undefined && callStatus !== this.callStatus) {
            this.callStatus = callStatus;
            this.state.videoInputs.update(this.makeVideoInputs());
        }
    }
    handleCameraShare(resp) {
        if (this.changingContentSource)
            return; // see comments in contentSource.set
        if (resp.CameraShare === undefined)
            return;
        this.cameraSharingInfo = resp.CameraShare;
        this.updateContentStateAndSource(this.sharingInfo, this.cameraSharingInfo, this.state.videoInputs.value);
        // Update the sendingContent property of the zoom call.
        const csv = Object.values(this.state.contentState.values);
        const sendingContent = csv.find(s => s === 'sending') !== undefined;
        this.state.calls.updateCall(this.zoomPrimaryKey, { sendingContent });
    }
    handleCapabilities(resp) {
        var _a, _b;
        this.isAirHostEnabled = !((_b = (_a = resp.Capabilities) === null || _a === void 0 ? void 0 : _a.is_Airhost_Disabled) !== null && _b !== void 0 ? _b : true);
        this.state.videoInputs.update(this.makeVideoInputs());
    }
    handleLayout(resp) {
        var _a, _b, _c;
        const layouts = {};
        if ((_a = resp.Layout) === null || _a === void 0 ? void 0 : _a.can_Switch_Wall_View) {
            layouts[this.layoutIds.gallery] = 'Gallery';
        }
        if ((_b = resp.Layout) === null || _b === void 0 ? void 0 : _b.can_Switch_Share_On_All_Screens) {
            layouts[this.layoutIds.shareAll] = 'ShareAll';
        }
        if ((_c = resp.Layout) === null || _c === void 0 ? void 0 : _c.can_Switch_Speaker_View) {
            layouts[this.layoutIds.speaker] = 'Speaker';
        }
        if (this.numberOfScreens === 1) {
            layouts[this.layoutIds.strip] = 'Strip';
        }
        this.availableLayouts = layouts;
        this.state.calls.updateLayouts(this.zoomPrimaryKey, Object.assign({}, this.availableLayouts));
    }
    handleNumberOfScreens(resp) {
        var _a, _b;
        this.numberOfScreens = (_b = (_a = resp.NumberOfScreens) === null || _a === void 0 ? void 0 : _a.NumberOfScreens) !== null && _b !== void 0 ? _b : 1;
        let changed = false;
        if (this.numberOfScreens === 1) {
            if (!(this.layoutIds.strip in this.availableLayouts)) {
                this.availableLayouts[this.layoutIds.strip] = 'Strip';
                changed = true;
            }
        }
        else {
            if (this.layoutIds.strip in this.availableLayouts) {
                delete this.availableLayouts[this.layoutIds.strip];
                changed = true;
            }
        }
        if (changed) {
            this.state.calls.updateLayouts(this.zoomPrimaryKey, Object.assign({}, this.availableLayouts));
        }
    }
    handleSharing(resp) {
        if (this.changingContentSource)
            return; // see comments in contentSource.set
        if (resp.Sharing === undefined)
            return;
        this.updateContentStateAndSource(resp.Sharing, this.cameraSharingInfo, this.state.videoInputs.value);
        this.sharingInfo = resp.Sharing;
        this.state.videoInputs.update(this.makeVideoInputs());
        this.updateContentStateAndSource(this.sharingInfo, this.cameraSharingInfo, this.state.videoInputs.value);
        // Update the sendingContent property of the zoom call.
        const csv = Object.values(this.state.contentState.values);
        const sendingContent = csv.find(s => s === 'sending') !== undefined;
        this.state.calls.updateCall(this.zoomPrimaryKey, { sendingContent });
    }
    handleSpotlightStatus(resp) {
        var _a, _b, _c, _d;
        const spotlightUserId = (_c = (_b = (_a = resp.SpotlightStatusNotification) === null || _a === void 0 ? void 0 : _a.user_id) === null || _b === void 0 ? void 0 : _b.toString()) !== null && _c !== void 0 ? _c : '';
        for (const [participantId, participant] of Object.entries((_d = this.state.callParticipants.values[this.zoomPrimaryKey]) !== null && _d !== void 0 ? _d : {})) {
            const spotlighted = spotlightUserId === participant.userId;
            if (spotlighted && !participant.spotlighted) {
                this.state.callParticipants.updateParticipant(this.zoomPrimaryKey, participantId, Object.assign(Object.assign({}, participant), { spotlighted }));
            }
            else if (!spotlighted && participant.spotlighted) {
                const newParticipant = Object.assign({}, participant);
                delete newParticipant.spotlighted;
                this.state.callParticipants.updateParticipant(this.zoomPrimaryKey, participantId, newParticipant);
            }
        }
    }
    handleZConfigurationCall(resp) {
        var _a, _b, _c, _d, _e, _f;
        const microphoneMute = (_b = (_a = resp.Call) === null || _a === void 0 ? void 0 : _a.Microphone) === null || _b === void 0 ? void 0 : _b.Mute;
        const sipMicrophoneMute = (_d = (_c = resp.SIPCall) === null || _c === void 0 ? void 0 : _c.Microphone) === null || _d === void 0 ? void 0 : _d.Mute;
        const cameraMute = (_f = (_e = resp.Call) === null || _e === void 0 ? void 0 : _e.Camera) === null || _f === void 0 ? void 0 : _f.Mute;
        if (microphoneMute !== undefined)
            this.state.mute.update(microphoneMute);
        if (sipMicrophoneMute !== undefined)
            this.state.mute.update(sipMicrophoneMute);
        if (cameraMute === undefined)
            return;
        for (const [key, callStatus] of Object.entries(this.state.calls.values)) {
            if (callStatus.callType === 'zoom') {
                this.state.calls.updateCall(key, { sendingVideo: !cameraMute });
            }
        }
    }
    isCallWaitingForPassword(call) {
        return (call.callState === 'ringing' &&
            (call.callExtendedState === 'password' || call.callExtendedState === 'passwordError'));
    }
    isVoipCallActive(call) {
        if (call === undefined)
            call = this.state.calls.values[this.voipPrimaryKey];
        return call.callState === 'connected' && call.callExtendedState !== 'hold';
    }
    lookupParticipantId(zoomParticipant, lookupParticipants) {
        var _a, _b;
        if (lookupParticipants === undefined) {
            lookupParticipants = (_a = this.state.callParticipants.values[this.zoomPrimaryKey]) !== null && _a !== void 0 ? _a : {};
        }
        for (const [id, participant] of Object.entries(lookupParticipants)) {
            if (participant.userId === ((_b = zoomParticipant.user_id) !== null && _b !== void 0 ? _b : 0).toString())
                return id;
        }
        return undefined;
    }
    makeCameras() {
        var _a;
        const cameras = {};
        for (const camera of this.zoomCameras) {
            const caps = {
                name: camera.name,
                hasCameraTracking: false,
                hasPan: true,
                hasTilt: true,
                hasZoom: true,
                location: 'near',
            };
            const configCameras = ((_a = this.config.cameras) !== null && _a !== void 0 ? _a : {});
            const config = Object.values(configCameras !== null && configCameras !== void 0 ? configCameras : {}).find(c => c.zoomId === camera.zoomId);
            if (config !== undefined) {
                if (config.name)
                    caps.name = config.name;
                if (config.extControl) {
                    caps.hasPan = true;
                    caps.hasTilt = true;
                    caps.hasZoom = true;
                    caps.extControl = {
                        move: true,
                        tracking: true,
                        preset: true,
                        equipmentId: config.extControl.equipmentId,
                        configId: config.extControl.configId,
                    };
                }
            }
            cameras[camera.zoomId] = caps;
        }
        return cameras;
    }
    makeDetected() {
        const channels = [];
        for (const input of this.audioInputs) {
            channels.push({
                alias: input.alias,
                name: input.name,
                zoomId: input.zoomId,
                type: 'audioInput',
                filters: {
                    microphone: true,
                },
            });
        }
        for (const output of this.audioOutputs) {
            channels.push({
                alias: output.alias,
                name: output.name,
                zoomId: output.zoomId,
                type: 'audioOutput',
                filters: {
                    speaker: true,
                },
            });
        }
        for (const camera of this.zoomCameras) {
            let isVideoInput = false;
            for (const info of this.hdmiDeviceInfo) {
                if (camera.vid === info.vid && camera.pid === info.pid) {
                    isVideoInput = true;
                }
            }
            channels.push({
                alias: camera.alias,
                name: camera.name,
                zoomId: camera.zoomId,
                type: 'usbCamera',
                filters: Object.assign({ camera: true }, (isVideoInput ? { videoInput: true } : undefined)),
            });
        }
        return { channels };
    }
    makeVideoInputs() {
        var _a, _b, _c, _d, _e, _f, _g;
        const inputs = {};
        const videoInputsConfig = (_a = this.config.videoInputs) !== null && _a !== void 0 ? _a : {};
        // videoConference
        if (this.videoInputIds.videoConference in videoInputsConfig) {
            inputs[this.videoInputIds.videoConference] = Object.assign({}, videoInputsConfig[this.videoInputIds.videoConference]);
        }
        else {
            this.log.error('videoConference input not defined in config');
            inputs[this.videoInputIds.videoConference] = {
                code: 'videoConference',
                icon: 'video_conference',
                mustInclude: true,
                name: this.framework.translate('Zoom Meeting'),
                isDynamic: true,
            };
        }
        // airPlay
        if (this.videoInputIds.airPlay in videoInputsConfig && this.isAirHostEnabled) {
            if (this.isAirHostEnabled) {
                inputs[this.videoInputIds.airPlay] = Object.assign({}, videoInputsConfig[this.videoInputIds.airPlay]);
            }
        }
        else if (this.sharingInfo.isAirHostClientConnected) {
            inputs[this.videoInputIds.airPlay] = {
                code: 'airPlay',
                icon: 'wireless',
                name: this.framework.translate('iPhone/iPad/Mac'),
                description: this.framework.translate('Using AirPlay'),
                farContent: true,
                isDynamic: true,
            };
        }
        if (this.videoInputIds.airPlay in inputs) {
            inputs[this.videoInputIds.airPlay].instructions = {
                type: 'zoomAirPlay',
                wifiNetwork: (_b = this.sharingInfo.wifiName) !== null && _b !== void 0 ? _b : 'Unknown',
                roomName: (_c = this.sharingInfo.serverName) !== null && _c !== void 0 ? _c : 'Unknown',
                password: (_d = this.sharingInfo.password) !== null && _d !== void 0 ? _d : 'Unknown',
            };
        }
        // laptop
        if (this.videoInputIds.laptop in videoInputsConfig) {
            inputs[this.videoInputIds.laptop] = Object.assign(Object.assign({}, videoInputsConfig[this.videoInputIds.laptop]), { instructions: {
                    type: 'zoomDesktop',
                    sharingKey: (_e = this.sharingInfo.directPresentationSharingKey) !== null && _e !== void 0 ? _e : 'Unknown',
                    meetingNumber: (_f = this.meetingId) !== null && _f !== void 0 ? _f : 'not in a meeting',
                    meetingPassword: (_g = this.meetingPassword) !== null && _g !== void 0 ? _g : 'not in a meeting',
                } });
        }
        // hdmi
        let hasHdmiInput = this.sharingInfo.isBlackMagicConnected;
        if (!hasHdmiInput) {
            for (const camera of this.zoomCameras) {
                for (const info of this.hdmiDeviceInfo) {
                    if (!camera.selected && camera.vid === info.vid && camera.pid === info.pid) {
                        hasHdmiInput = true;
                        break;
                    }
                }
                if (hasHdmiInput)
                    break;
            }
        }
        // cameras
        for (const [id, videoInput] of Object.entries(videoInputsConfig)) {
            if (videoInput.code.startsWith('camera.')) {
                const camera = this.zoomCameras.find(camera => `camera.${camera.zoomId}` === videoInput.code);
                if (camera) {
                    inputs[id] = Object.assign({}, videoInput);
                    if (this.callStatus !== 'IN_MEETING' || this.meetingType !== 'NORMAL') {
                        inputs[id].unavailableReason =
                            'notInZoomCall';
                    }
                }
            }
        }
        if (this.videoInputIds.hdmi in videoInputsConfig) {
            if (hasHdmiInput) {
                // Deep copy for source property.
                inputs[this.videoInputIds.hdmi] = Sutl.deepCopy(videoInputsConfig[this.videoInputIds.hdmi]);
            }
        }
        else if (this.sharingInfo.isSharingBlackMagic) {
            inputs[this.videoInputIds.hdmi] = {
                code: 'hdmi',
                icon: 'laptop',
                name: this.framework.translate('Laptop'),
                description: this.framework.translate('Using Cable'),
                farContent: true,
                isDynamic: true,
            };
        }
        // If the content source got removed, reset it to videoConference.
        if (this.findVideoInputId(this.state.contentSource.value, inputs) === undefined &&
            this.findVideoInputId('videoConference', this.state.videoInputs.value) !== undefined) {
            this.state.contentSource.update('videoConference');
        }
        return inputs;
    }
    match(resp, type, topKey, paths, sync, ok = true) {
        var _a, _b;
        if (resp.type !== type)
            return undefined;
        if (resp.topKey !== topKey)
            return undefined;
        if (sync !== undefined && resp.Sync !== sync)
            return undefined;
        if (ok && ((_b = (_a = resp === null || resp === void 0 ? void 0 : resp.Status) === null || _a === void 0 ? void 0 : _a.state) !== null && _b !== void 0 ? _b : '') !== 'OK')
            return undefined;
        if (paths !== undefined) {
            if (!Array.isArray(paths))
                paths = [paths];
            for (const path of paths) {
                if (Sutl.getProp(resp, path) === undefined)
                    return undefined;
            }
        }
        return resp;
    }
    onCallChanged(key, prev) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        this.state.cameraSelfViewAvailable.update(this.computeSelfViewAvailable(this.state.calls.values));
        const call = this.state.calls.values[key];
        let newCall = Object.assign({}, call);
        let changed = false;
        let shouldQueryMute = false;
        const prevCallState = (_a = prev === null || prev === void 0 ? void 0 : prev.callState) !== null && _a !== void 0 ? _a : 'disconnected';
        if (call === undefined)
            throw new Error(`${key} call undefined`);
        this.log.debug(`onCallChanged: ${key}: ${prevCallState} -> ${call.callState}`);
        // Clear transitional callExtendedStates if we successfully connect.
        if (prevCallState !== 'connected' &&
            call.callState === 'connected' &&
            call.callExtendedState !== undefined &&
            call.callExtendedState !== 'hold') {
            if (this.isRecordingConsentRequired) {
                newCall.callExtendedState = 'recordingConsentRequired';
            }
            else {
                delete newCall.callExtendedState;
            }
            changed = true;
        }
        // Clear callExtendedState on disconnect.
        if (prevCallState !== 'disconnected' &&
            call.callState === 'disconnected' &&
            call.callExtendedState !== undefined &&
            call.callExtendedState !== 'invalidDialString') {
            delete newCall.callExtendedState;
            changed = true;
        }
        if (key === this.zoomPrimaryKey) {
            // If this is an incoming Zoom all and there's an active SIP call, then set its
            // callExtendedState to "answerWillEnd".
            if (call.callState === 'ringing' &&
                call.direction === 'incoming' &&
                ((_b = this.state.calls.values[this.voipPrimaryKey]) === null || _b === void 0 ? void 0 : _b.callState) === 'connected') {
                newCall.callExtendedState = 'answerWillEnd';
                changed = true;
            }
            // If the call is being disconnected...
            //
            // TODO: is there a status message we can use for triggering this?
            if (prevCallState !== 'disconnected' && call.callState === 'disconnected') {
                if (((_c = this.state.calls.values[this.zoomSecondaryKey]) === null || _c === void 0 ? void 0 : _c.callState) === 'ringing') {
                    // If there is an incoming Zoom call, then swap the zoom primary and secondary
                    // keys and update the meetingId with the incoming call's meetingId.
                    [this.zoomPrimaryKey, this.zoomSecondaryKey] = [
                        this.zoomSecondaryKey,
                        this.zoomPrimaryKey,
                    ];
                    this.meetingId = (_d = this.state.calls.values[this.zoomSecondaryKey]) === null || _d === void 0 ? void 0 : _d.dialString;
                    this.displayDialString = this.formatMeetingId(this.meetingId);
                }
                else {
                    // Otherwise, just clear the meetingId.
                    this.meetingId = undefined;
                    this.meetingPassword = undefined;
                    this.displayDialString = 'Unknown';
                }
            }
            if (call.callState === 'disconnected' && prevCallState !== 'disconnected') {
                // Reset fields in preparation for the next call. We must do this since we're
                // reusing the call state (unlike most drivers, which remove the old call state
                // and generate a new one for each call).
                newCall = Object.assign(Object.assign({}, newCall), callExtras.zoom);
                newCall.layouts = {
                    current: Object.keys(this.availableLayouts).sort()[0],
                    available: Object.assign({}, this.availableLayouts),
                };
                this.state.callParticipants.clear(this.zoomPrimaryKey);
                // Also query mute state.
                shouldQueryMute = true;
            }
        }
        else if (key === this.voipPrimaryKey) {
            // If this is an incoming SIP call and there's an active Zoom call, then set its
            // callExtendedState to "answerWillHnd".
            if (call.callState === 'ringing' &&
                call.direction === 'incoming' &&
                ((_e = this.state.calls.values[this.zoomPrimaryKey]) === null || _e === void 0 ? void 0 : _e.callState) === 'connected') {
                newCall.callExtendedState = 'answerWillHold';
                changed = true;
            }
            // If we're accepting an incoming SIP call and there's an active Zoom call, then set
            // the Zoom call's callExtendedState to "hold".
            //
            // TODO: is there a status message we can use for triggering this?
            if (call.callState === 'connected' &&
                call.direction === 'incoming' &&
                ((_f = this.state.calls.values[this.zoomPrimaryKey]) === null || _f === void 0 ? void 0 : _f.callState) === 'connected') {
                this.state.calls.updateCall(this.zoomPrimaryKey, { callExtendedState: 'hold' });
            }
            // If we're disconnecting a SIP call and there's a Zoom call on hold, then clear the
            // Zoom call's callExtendedState.
            if (call.callState === 'disconnected' &&
                prevCallState !== 'disconnected' &&
                ((_g = this.state.calls.values[this.zoomPrimaryKey]) === null || _g === void 0 ? void 0 : _g.callState) === 'connected') {
                this.state.calls.updateCallExtendedState(this.zoomPrimaryKey, undefined);
            }
            if (this.isVoipCallActive() !== this.isVoipCallActive(prev))
                shouldQueryMute = true;
            // TODO: Handle the case where there's a Zoom call with an invalid meeting password
            // waiting on the correct one, then an incomimng SIP call happens, puts the Zoom call
            // on hold, then gets ended. The Zoom call should go back to callState = "connecting"
            // and callExtendedState = "password" or "passwordError", but I'm not sure if it will.
        }
        if (call.callType === 'zoom' &&
            call.canEndMeeting && // use canEndMeeting as proxy for isHost
            Object.keys((_h = this.state.callParticipants.values[key]) !== null && _h !== void 0 ? _h : {}).length > 3) {
            if (!call.canSpotlight) {
                newCall.canSpotlight = true;
                changed = true;
            }
        }
        else {
            if (call.canSpotlight) {
                newCall.canSpotlight = false;
                changed = true;
            }
        }
        if (changed)
            this.state.calls.update(key, newCall);
        if (shouldQueryMute)
            this.queryMute(); // don't await
        this.state.disabledCalls.update(this.makeDisabledCalls());
    }
    makeDisabledCalls() {
        var _a, _b;
        const voipDisabled = this.config.audioDialingEnabled === true &&
            ((_a = this.state.calls.values[this.zoomPrimaryKey]) === null || _a === void 0 ? void 0 : _a.callState) !== 'disconnected';
        return {
            voip: voipDisabled ? 1 : 0,
            zoom: ((_b = this.state.calls.values[this.voipPrimaryKey]) === null || _b === void 0 ? void 0 : _b.callState) === 'disconnected' ? 0 : 1,
        };
    }
    onCameraContinueTimeout() {
        if (this.cameraMoveDirection === undefined) {
            this.log.error('onCameraContinueTimeout: direction is undefined');
            this.cameraContinueTimeout.clear();
            return;
        }
        if (!this.socket.hasCommands('cameraContinue')) {
            const tags = ['cameraContinue'];
            this.setCameraControl({ state: 'Continue', action: this.cameraMoveDirection }, tags);
        }
        this.cameraContinueTimeout.set(this.onCameraContinueTimeout, 35);
    }
    onCameraMoveTimeout() {
        this.log.error('onCameraMoveTimeout: stop command did not get issued');
        this.cameraMoveTimeout.clear();
        if (this.cameraMoveDirection === undefined) {
            this.log.error('onCameraMoveTimeout: direction is undefined');
            return;
        }
        const tags = ['set.cameraMove'];
        this.setCameraControl({ state: 'Stop', action: this.cameraMoveDirection }, tags);
    }
    parseAudioInputs(resp) {
        var _a, _b, _c, _d;
        const channels = [];
        const audioInputs = (_a = resp['Audio Input Line']) !== null && _a !== void 0 ? _a : [];
        for (const input of audioInputs) {
            channels.push({
                alias: (_b = input.Alias) !== null && _b !== void 0 ? _b : 'Unknown',
                name: (_c = input.Name) !== null && _c !== void 0 ? _c : 'Unknown',
                selected: (_d = input.Selected) !== null && _d !== void 0 ? _d : false,
                zoomId: input.id,
            });
        }
        // Sort by zoom id to force consistent order. This prevents unnecessary state updates.
        return channels.sort((a, b) => a.zoomId.localeCompare(b.zoomId));
    }
    parseAudioOutputs(resp) {
        var _a, _b, _c, _d;
        const channels = [];
        const audioOutputs = (_a = resp['Audio Output Line']) !== null && _a !== void 0 ? _a : [];
        for (const output of audioOutputs) {
            channels.push({
                alias: (_b = output.Alias) !== null && _b !== void 0 ? _b : 'Unknown',
                name: (_c = output.Name) !== null && _c !== void 0 ? _c : 'Unknown',
                selected: (_d = output.Selected) !== null && _d !== void 0 ? _d : false,
                zoomId: output.id,
            });
        }
        // Sort by zoom id to force consistent order. This prevents unnecessary state updates.
        return channels.sort((a, b) => a.zoomId.localeCompare(b.zoomId));
    }
    parseBookingsListResult(resp, update = true) {
        var _a, _b, _c;
        const events = [];
        for (const booking of (_a = resp.BookingsListResult) !== null && _a !== void 0 ? _a : []) {
            const event = {
                summary: (_b = booking.meetingName) !== null && _b !== void 0 ? _b : 'Unknown',
                start: moment((_c = booking.startTime) !== null && _c !== void 0 ? _c : 0).valueOf(),
            };
            if (booking.creatorEmail !== undefined) {
                Sutl.setProp(event, 'organizer.emailAddress', booking.creatorEmail);
            }
            if (booking.creatorName !== undefined) {
                Sutl.setProp(event, 'organizer.name', booking.creatorName);
            }
            if (booking.endTime !== undefined) {
                event.end = moment(booking.endTime).valueOf();
            }
            if (booking.location !== undefined) {
                event.location = { name: booking.location };
            }
            if (booking.meetingNumber !== undefined && booking.meetingNumber !== '0') {
                event.callMethods = {
                    a0: {
                        callType: 'zoom',
                        dialString: booking.meetingNumber,
                    },
                };
            }
            events.push(event);
        }
        // Sort by start time to force consistent order. This prevents unnecessary state updates.
        events.sort((a, b) => b.start - a.start);
        this.bookingsUpdated = false;
        const calendar = { events: events, eventWindowDays: 1 };
        if (update)
            this.state.calendar.update(calendar);
        return calendar;
    }
    parseCameras(resp) {
        var _a, _b, _c, _d;
        const cameras = [];
        const cameraInfo = (_a = resp['Video Camera Line']) !== null && _a !== void 0 ? _a : [];
        for (const info of cameraInfo) {
            const camera = {
                alias: (_b = info.Alias) !== null && _b !== void 0 ? _b : 'Unknown',
                name: (_c = info.Name) !== null && _c !== void 0 ? _c : 'Unknown',
                selected: (_d = info.Selected) !== null && _d !== void 0 ? _d : false,
                zoomId: info.id,
            };
            const match = /usb:([a-f\d]+):([a-f\d]+)/i.exec(camera.alias);
            if (match) {
                camera.vid = parseInt(match[1], 16);
                camera.pid = parseInt(match[2], 16);
            }
            cameras.push(camera);
        }
        // Sort by zoom id to force consistent order. This prevents unnecessary state updates.
        return cameras.sort((a, b) => a.zoomId.localeCompare(b.zoomId));
    }
    parseContact(contact) {
        if (contact.jid === undefined) {
            this.log.error("contact doesn't have jid property");
            return undefined;
        }
        const entry = {
            contacts: {
                a0: {
                    name: 'Zoom',
                    number: contact.jid,
                    type: 'zoom',
                },
            },
        };
        if (contact.firstName)
            entry.firstName = contact.firstName;
        if (contact.lastName)
            entry.lastName = contact.lastName;
        if (contact.screenName)
            entry.displayName = contact.screenName;
        if (contact.avatarURL)
            entry.picture = contact.avatarURL;
        if (typeof contact.onDesktop === 'boolean')
            entry.onDesktop = contact.onDesktop;
        if (typeof contact.onMobile === 'boolean')
            entry.onMobile = contact.onMobile;
        if (typeof contact.isZoomRoom === 'boolean')
            entry.isRoom = contact.isZoomRoom;
        if (contact.presence) {
            const value = this.presenceMap[contact.presence];
            if (value !== undefined) {
                entry.presence = value;
            }
            else {
                this.log.error(`unexpected presence value: ${contact.presence}`);
            }
        }
        const key = contact.jid.replace(this.jidReplaceRegex, '-');
        return [key, entry];
    }
    parseLayoutStyle(resp) {
        var _a, _b, _c, _d, _e;
        const layoutStyle = (_c = (_b = (_a = resp.Client) === null || _a === void 0 ? void 0 : _a.Call) === null || _b === void 0 ? void 0 : _b.Layout) === null || _c === void 0 ? void 0 : _c.Style;
        let layoutId;
        const call = this.state.calls.values[this.zoomPrimaryKey];
        const currentLayoutId = (_e = (_d = call === null || call === void 0 ? void 0 : call.layouts) === null || _d === void 0 ? void 0 : _d.current) !== null && _e !== void 0 ? _e : 'unknown';
        if (layoutStyle === undefined)
            return currentLayoutId;
        for (const [id, layout] of Object.entries(this.availableLayouts)) {
            if (layout === layoutStyle) {
                layoutId = id;
                break;
            }
        }
        if (layoutId === undefined) {
            // If a sharing-only meeting is started with HDMI content, the Zoom API
            // reports can_Switch_Share_On_All_Screens as false, but then send an event
            // reporting that the layout is ShareAll. This code handles inserting the
            // layout into the available layouts in that case and any other cases where
            // the Zoom API is inconsistent.
            this.log.info(`unrecognized layout style: ${layoutStyle}, adding to available layouts`);
            for (const [id, layout] of Object.entries(this.allLayouts)) {
                if (layout === layoutStyle) {
                    this.availableLayouts[id] = layout;
                    layoutId = id;
                    break;
                }
            }
            if (layoutId === undefined) {
                this.log.error(`unknown layout style: ${layoutStyle}`);
            }
            else {
                this.state.calls.updateLayouts(this.zoomPrimaryKey, Object.assign({}, this.availableLayouts));
            }
        }
        return layoutId !== null && layoutId !== void 0 ? layoutId : currentLayoutId;
    }
    parseParticipant(p, lookupParticipants) {
        var _a, _b, _c, _d;
        if (lookupParticipants === undefined) {
            lookupParticipants = (_a = this.state.callParticipants.values[this.zoomPrimaryKey]) !== null && _a !== void 0 ? _a : {};
        }
        let limit = false;
        let participantId;
        const participant = {
            audioMuted: p['audio_status state'] === 'AUDIO_MUTED',
            hasAudioMute: true,
            hasVideoMute: p.is_video_can_mute_byHost === true,
            isCohost: p.isCohost === true,
            isHost: p.is_host === true,
            sendingVideo: p['video_status is_sending'] === true,
            userId: ((_b = p.user_id) !== null && _b !== void 0 ? _b : 0).toString(),
            userIdType: 'zoom',
        };
        if (p.user_name)
            participant.displayName = p.user_name;
        if (p.avatar_url)
            participant.picture = p.avatar_url;
        if (p.is_myself)
            participant.isMe = true;
        if (((_c = p.hand_status) === null || _c === void 0 ? void 0 : _c.is_raise_hand) !== undefined) {
            participant.isHandRaised = p.hand_status.is_raise_hand === true;
        }
        if (((_d = p.hand_status) === null || _d === void 0 ? void 0 : _d['is_raise_hand: ']) !== undefined) {
            participant.isHandRaised = p.hand_status['is_raise_hand: '] === true;
        }
        participantId = this.lookupParticipantId(p, lookupParticipants);
        if (participantId === undefined) {
            participantId = Sutl.generateFirebaseId();
            limit =
                this.config.callParticipantsLimit !== undefined &&
                    !participant.isHost &&
                    !participant.isCohost &&
                    Object.keys(lookupParticipants).length >= this.config.callParticipantsLimit;
        }
        return [participantId, participant, limit];
    }
    queryCalls(callInfo = true, extras = false, poll = false) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            if (callInfo && !this.socket.hasCommands('get.callInfo')) {
                // Sometimes the ZR doesn't respond to the call info command for a long time, so we
                // retry several times. We send a dummy command (appVersion) along with the call info
                // command so we'll at least get something back, thus preventing an idle timeout.
                const tags = poll ? ['poll', 'get.callInfo'] : ['callInfo'];
                const resp = (yield this.socket.send('zConfiguration Client appVersion\r\nzCommand Call Info', (resp) => {
                    if (this.match(resp, 'zCommand', 'CallInfoResult', [], true, false) ||
                        this.match(resp, 'zCommand', 'InfoResult', [], true, false) ||
                        this.match(resp, 'zEvent', 'NeedWaitForHost', [], undefined)) {
                        return resp;
                    }
                    return undefined;
                }, tags, { sendTimeoutMs: 2000, retryAttempts: poll ? 1 : 4 }));
                const call = this.state.calls.values[this.zoomPrimaryKey];
                if (call.callState === 'ringing' &&
                    (call.callExtendedState === 'password' ||
                        call.callExtendedState === 'passwordError' ||
                        call.callExtendedState === 'passwordSubmitted') &&
                    ((_a = resp.Status) === null || _a === void 0 ? void 0 : _a.state) === 'Error' &&
                    ((_b = resp.Status) === null || _b === void 0 ? void 0 : _b.message) === 'Result reason: "Not in meeting"') {
                    return;
                }
                if (((_c = resp.Status) === null || _c === void 0 ? void 0 : _c.state) !== 'OK') {
                    if (extras) {
                        this.state.calls.disconnect(this.zoomPrimaryKey, {
                            layouts: {
                                current: Object.keys(this.availableLayouts).sort()[0],
                                available: Object.assign({}, this.availableLayouts),
                            },
                        });
                    }
                    else {
                        this.state.calls.disconnect(this.zoomPrimaryKey);
                    }
                    return;
                }
                if (resp.type === 'zCommand') {
                    this.handleCallInfo(resp);
                }
                else {
                    this.state.calls.connect(this.zoomPrimaryKey);
                }
            }
            if (!this.socket.hasCommands('get.callStatus')) {
                const cmd = 'zStatus Call Status';
                const type = 'zStatus';
                const topKey = 'Call';
                const tags = poll ? ['poll', 'get.callStatus'] : ['get.callStatus'];
                const resp = yield this.send(cmd, type, topKey, tags, true, true);
                this.handleCallStatus(resp);
            }
            if (extras) {
                if (!this.socket.hasCommands('get.callLayout')) {
                    const cmd = 'zStatus Call Layout';
                    const type = 'zStatus';
                    const topKey = 'Layout';
                    const tags = poll ? ['poll', 'get.callLayout'] : ['get.callLayout'];
                    const resp = yield this.send(cmd, type, topKey, tags, true, true);
                    this.handleLayout(resp);
                }
                if (!this.socket.hasCommands('get.callCameraMute')) {
                    const cmd = 'zConfiguration Call Camera Mute';
                    const type = 'zConfiguration';
                    const topKey = 'Call';
                    const tags = poll ? ['poll', 'get.callCameraMute'] : ['get.callCameraMute'];
                    const resp = yield this.send(cmd, type, topKey, tags, true, true);
                    this.handleZConfigurationCall(resp);
                }
                if (!this.socket.hasCommands('get.callLayoutStyle')) {
                    const cmd = 'zConfiguration Call Layout Style';
                    const type = 'zConfiguration';
                    const topKey = 'Client';
                    const tags = poll ? ['poll', 'get.callLayoutStyle'] : ['get.callLayoutStyle'];
                    const resp = yield this.sendWithCommonResult(cmd, type, topKey, tags, true, true);
                    if (resp !== undefined)
                        this.handleCallLayoutStyle(resp);
                }
                if (!this.socket.hasCommands('get.callListParticipants')) {
                    const cmd = 'zCommand Call ListParticipants';
                    const type = 'zCommand';
                    const topKey = ['CallListParticipantsResult', 'ListParticipantsResult'];
                    const tags = poll
                        ? ['poll', 'get.callListParticipants']
                        : ['get.callListParticipants'];
                    const resp = yield this.send(cmd, type, topKey, tags, true, false);
                    this.handleCallListParticipants(resp);
                }
            }
        });
    }
    queryMute() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.socket.hasCommands('get.mute'))
                return;
            if (this.isVoipCallActive()) {
                yield this.socket.sendAsync('zConfiguration SIPCall Microphone Mute', ['get.mute']);
            }
            else {
                yield this.socket.sendAsync('zConfiguration Call Microphone Mute', ['get.mute']);
            }
        });
    }
    queryParticipants() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.socket.hasCommands('get.participants'))
                return;
            yield this.socket.sendAsync('zCommand Call ListParticipants', ['get.listParticipants']);
        });
    }
    querySharing() {
        return __awaiter(this, void 0, void 0, function* () {
            this.handleSharing(yield this.getSharing(['get.sharing']));
            this.handleCameraShare(yield this.getCameraShare(['get.cameraShare']));
        });
    }
    send(command, type, topKeys, tags = [], sync, ok = true, options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!Array.isArray(topKeys))
                topKeys = [topKeys];
            return (yield this.socket.send(command, (resp) => {
                for (const topKey of topKeys) {
                    if (this.match(resp, type, topKey, [], sync, ok))
                        return resp;
                }
                return undefined;
            }, tags, options));
        });
    }
    sendWithCommonResult(command, type, topKeys, tags = [], sync, ok = true) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!Array.isArray(topKeys))
                topKeys = [topKeys];
            const resp = (yield this.socket.send(command, (resp) => {
                for (const topKey of topKeys) {
                    if (this.match(resp, type, topKey, [], sync, ok))
                        return resp;
                }
                if (this.match(resp, 'zCommand', 'CommonResult', [], true, false))
                    return resp;
                return undefined;
            }, tags));
            return resp.topKey === 'CommonResult' ? undefined : resp;
        });
    }
    setAudioInputSelected(id, tags = []) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = `zConfiguration Audio Input selectedId: ${id}`;
            const type = 'zConfiguration';
            const topKey = 'Audio';
            // The Audio Input response reports Sync as false. This is a bug in the ZRCSAPI, but we
            // work around it by accepting any value for Sync.
            const resp = (yield this.send(cmd, type, topKey, tags, undefined));
            return (_b = (_a = resp.Audio) === null || _a === void 0 ? void 0 : _a.Input) === null || _b === void 0 ? void 0 : _b.selectedId;
        });
    }
    setAudioInputVolume(value, tags = []) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = `zConfiguration Audio Input volume: ${value}`;
            const type = 'zConfiguration';
            const topKey = 'Audio';
            // The Audio Input response reports Sync as false. This is a bug in the ZRCSAPI, but we
            // work around it by accepting any value for Sync.
            const resp = (yield this.send(cmd, type, topKey, tags, undefined));
            return (_b = (_a = resp.Audio) === null || _a === void 0 ? void 0 : _a.Input) === null || _b === void 0 ? void 0 : _b.volume;
        });
    }
    setAudioOutputSelected(id, tags = []) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = `zConfiguration Audio Output selectedId: ${id}`;
            const type = 'zConfiguration';
            const topKey = 'Audio';
            // The Audio Output response reports Sync as false. This is a bug in the ZRCSAPI, but we
            // work around it by accepting any value for Sync.
            const resp = (yield this.send(cmd, type, topKey, tags, undefined));
            return (_b = (_a = resp.Audio) === null || _a === void 0 ? void 0 : _a.Output) === null || _b === void 0 ? void 0 : _b.selectedId;
        });
    }
    setAudioOutputVolume(value, tags = []) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = `zConfiguration Audio Output Volume: ${value}`;
            const type = 'zConfiguration';
            const topKey = 'Audio';
            // The Audio Output response reports Sync as false. This is a bug in the ZRCSAPI, but we
            // work around it by accepting any value for Sync.
            const resp = yield this.send(cmd, type, topKey, tags, undefined);
            return (_b = (_a = resp.Audio) === null || _a === void 0 ? void 0 : _a.Output) === null || _b === void 0 ? void 0 : _b.volume;
        });
    }
    setCallDisconnect(endMeeting, tags = []) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = `zCommand Call ${endMeeting ? 'Disconnect' : 'Leave'}`;
            const resp = yield this.send(cmd, 'zCommand', 'CallDisconnectResult', tags, true, false);
            if (((_a = resp.Status) === null || _a === void 0 ? void 0 : _a.state) !== 'OK')
                this.queryCalls();
            this.queryMute();
        });
    }
    setCallHostClaim(hostKey, tags = []) {
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = `zCommand Call HostClaim Key: ${hostKey}`;
            // This command takes longer to respond in our testing.
            yield this.send(cmd, 'zCommand', 'CallHostClaim', tags, true, false, {
                sendTimeoutMs: 4000,
            });
        });
    }
    setCallInvite(users, tags = []) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (users.length === 0)
                return;
            let cmd = 'zCommand Call invite';
            users.forEach(user => (cmd += ` user: ${user}`));
            const resp = yield this.send(cmd, 'zCommand', 'InviteCallResult', tags, true, false);
            if (((_a = resp.Status) === null || _a === void 0 ? void 0 : _a.state) === 'OK') {
                if (this.callStatus === 'NOT_IN_MEETING')
                    this.meetingType = 'NORMAL';
            }
            else {
                this.queryCalls();
            }
        });
    }
    setCallInviteH323(invitees, tags = []) {
        return __awaiter(this, void 0, void 0, function* () {
            yield Promise.all(invitees.map((inv) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                let cmd = `zCommand Call InviteH323Room Address: ${inv.address}`;
                cmd += ` cancel: ${inv.cancel ? 'On' : 'Off'}`;
                const type = 'zCommand';
                const topKey = 'CallInviteH323Room';
                const resp = yield this.send(cmd, type, topKey, tags, true, false);
                if (((_a = resp.Status) === null || _a === void 0 ? void 0 : _a.state) === 'OK') {
                    if (this.callStatus === 'NOT_IN_MEETING')
                        this.meetingType = 'NORMAL';
                }
                else {
                    this.queryCalls();
                }
            })));
        });
    }
    setCallInviteSip(invitees, tags = []) {
        return __awaiter(this, void 0, void 0, function* () {
            yield Promise.all(invitees.map((inv) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                let cmd = `zCommand Call InviteSipRoom Address: ${inv.address}`;
                cmd += ` cancel: ${inv.cancel ? 'On' : 'Off'}`;
                const type = 'zCommand';
                const topKey = 'CallInviteSipRoom';
                const resp = yield this.send(cmd, type, topKey, tags, true, false);
                if (((_a = resp.Status) === null || _a === void 0 ? void 0 : _a.state) === 'OK') {
                    if (this.callStatus === 'NOT_IN_MEETING')
                        this.meetingType = 'NORMAL';
                }
                else {
                    this.queryCalls();
                }
            })));
        });
    }
    setCallReject(callType, jid, tags = []) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = callType === 'voip'
                ? `zCommand Reject SIPCall CallID: ${jid}`
                : `zCommand Call Reject callerJID: ${jid}`;
            const resp = yield this.send(cmd, 'zCommand', 'CallAcceptRejectResult', tags, true, false);
            if (((_a = resp.Status) === null || _a === void 0 ? void 0 : _a.state) === 'OK') {
                // If we're in a sharing meeting and reject an incoming call, the ZR call status
                // doesn't change. In that case, we disconnect the call here.
                if (this.meetingType === 'SHARING_LAPTOP' && this.callStatus === 'IN_MEETING') {
                    this.state.calls.disconnect(callType === 'zoom' ? this.zoomPrimaryKey : this.voipPrimaryKey);
                }
                // Rejecting the call doesn't result in any API messages that trigger us to disconnect
                // the call. So, we query the relevant commands here.
                this.queryCalls();
            }
            else {
                this.queryCalls();
            }
        });
    }
    setCallShareCamera(id, status, tags = []) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = `zCommand Call ShareCamera id: ${id} Status: ${status ? 'on' : 'off'}`;
            const resp = yield this.send(cmd, 'zCommand', 'ShareCameraResult', tags, true, false);
            if (((_a = resp.Status) === null || _a === void 0 ? void 0 : _a.state) !== 'OK')
                this.queryCalls();
            this.queryMute();
        });
    }
    setCameraControl(control, tags = []) {
        return __awaiter(this, void 0, void 0, function* () {
            let cmd = `zCommand Call CameraControl Id: ${'id' in control ? control.id : '0'}`;
            if ('state' in control)
                cmd += ` State: ${control.state}`;
            if ('action' in control)
                cmd += ` Action: ${control.action}`;
            if ('speed' in control)
                cmd += ` Speed: ${control.speed}`;
            this.framework.suspendPolling(2000);
            if (control.state === 'Continue') {
                try {
                    yield this.socket.sendAsync(cmd, tags);
                }
                catch (error) {
                    if (error.code !== 'commandRemoved')
                        throw error;
                }
            }
            else {
                yield this.send(cmd, 'zCommand', 'CameraControl', tags, true);
            }
        });
    }
    setClientName(tags = []) {
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = 'zConfiguration Client deviceSystem: Mira Connect';
            yield this.send(cmd, 'zConfiguration', 'Configuration', tags, true);
        });
    }
    setClientVersion(tags = []) {
        return __awaiter(this, void 0, void 0, function* () {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const inaraVersion = window.inaraVersion;
            const cmd = `zConfiguration Client appVersion: ${inaraVersion}`;
            yield this.send(cmd, 'zConfiguration', 'Configuration', tags, true);
        });
    }
    setDialJoin(meetingNumber, password, tags = []) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            let cmd = `zCommand Dial Join meetingNumber: ${meetingNumber}`;
            if (password)
                cmd += ` password: ${password}`;
            const resp = yield this.socket.send(cmd, (resp) => {
                if (this.match(resp, 'zCommand', 'DialJoinResult', [], true, false))
                    return resp;
                if (this.match(resp, 'zStatus', 'onUnsupported Command', [], true, false)) {
                    return resp;
                }
                return undefined;
            }, tags);
            if (((_a = resp.Status) === null || _a === void 0 ? void 0 : _a.state) === 'OK') {
                this.meetingType = 'NORMAL';
            }
            else {
                this.queryCalls();
            }
        });
    }
    setDialPhoneCallOut(dialString, tags = []) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const resp = (yield this.socket.send(`zCommand Dial PhoneCallOut Number: ${dialString}`, (resp) => {
                if (!resp.Sync)
                    return undefined;
                if ((resp.type === 'zCommand' && resp.topKey === 'DialPhoneCallOut') ||
                    resp.type === 'zStatus' // some errors are return as status messages
                ) {
                    return resp;
                }
                return undefined;
            }));
            if (((_a = resp.Status) === null || _a === void 0 ? void 0 : _a.state) !== 'OK') {
                if (((_b = this.state.calls.values[this.voipPrimaryKey]) === null || _b === void 0 ? void 0 : _b.callState) === 'ringing') {
                    this.state.calls.disconnect(this.voipPrimaryKey);
                }
            }
        });
    }
    setDialPhoneHangup(jid, tags = []) {
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = `zCommand Dial PhoneHangUp CallId: ${jid}`;
            yield this.send(cmd, 'zCommand', 'DialPhoneHangUp', tags, true);
        });
    }
    setDialSharing(value, tags = []) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const display = value === 'airPlay' ? 'IOS' : value === 'laptop' ? 'Laptop' : 'None';
            const cmd = `zCommand Dial Sharing Duration: 30 Displaystate: ${display} Password: `;
            const resp = yield this.send(cmd, 'zCommand', 'DialSharingResult', tags, true, false);
            if (((_a = resp.Status) === null || _a === void 0 ? void 0 : _a.state) === 'OK') {
                this.meetingType = 'SHARING_LAPTOP';
            }
            else {
                this.queryCalls();
            }
        });
    }
    setDialStartPmi(tags = []) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = 'zCommand Dial StartPmi Duration: 30';
            const resp = yield this.send(cmd, 'zCommand', 'DialStartPmiResult', tags, true, false);
            if (((_a = resp.Status) === null || _a === void 0 ? void 0 : _a.state) === 'OK') {
                this.meetingType = 'NORMAL';
            }
            else {
                this.queryCalls();
            }
        });
    }
    setDisconnectSharingCall(tags = []) {
        return __awaiter(this, void 0, void 0, function* () {
            // Use Promise.all to send these two commands back-to-back.
            yield Promise.all([
                this.setCallDisconnect(true, tags),
                (() => __awaiter(this, void 0, void 0, function* () {
                    // The disconnect command will trigger a call status message telling us we are no
                    // longer in a meeting. However, that message is often delayed. The following trick
                    // forces us to wait for the call status message before sending any more commands.
                    // This prevents suprious call transitions.
                    //
                    // We send a dummy command, and instead of waiting for its reponse, we wait for the
                    // call status message. We can't send a blank command as the dummy command because
                    // that causes the ZR to terminate the connection.
                    if (this.callStatus !== 'NOT_IN_MEETING') {
                        const resp = (yield this.socket.send('zConfiguration Client appVersion', (resp) => {
                            if (resp.type === 'zStatus' &&
                                resp.topKey === 'Call' &&
                                Sutl.getProp(resp, 'Call.Status') !== undefined) {
                                return resp;
                            }
                            return undefined;
                        }, tags));
                        this.handleCallStatus(resp);
                    }
                }))(),
            ]);
            this.queryMute();
        });
    }
    setHdmiSharing(start, tags = []) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = `zCommand Call Sharing HDMI ${start ? 'Start' : 'Stop'}`;
            const resp = yield this.send(cmd, 'zCommand', 'CallSharingHDMIResult', tags, true, false);
            if (((_a = resp.Status) === null || _a === void 0 ? void 0 : _a.state) !== 'OK')
                this.queryCalls();
            this.queryMute();
        });
    }
    setInvite(users, tags = []) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (users.length === 0)
                return;
            let cmd = 'zCommand Invite Duration: 30';
            users.forEach(user => (cmd += ` user: ${user}`));
            const resp = yield this.send(cmd, 'zCommand', 'InviteResult', tags, true, false);
            if (((_a = resp.Status) === null || _a === void 0 ? void 0 : _a.state) === 'OK') {
                if (this.callStatus === 'NOT_IN_MEETING')
                    this.meetingType = 'NORMAL';
            }
            else {
                this.queryCalls();
            }
        });
    }
    setPhonebookSubscribe(tags = []) {
        return __awaiter(this, void 0, void 0, function* () {
            // Send asynchronously since we don't need the result and the ZR can either respond with
            // a zCommand PhonebookSubscribeResult, a zStatus onUnsupportedCommand, or a
            // zStatus onUnsupported Command. (And it's a pain to parse all three when we don't need
            // them.)
            const cmd = 'zCommand Phonebook Subscribe Offset: 0 Limit: 500';
            yield this.socket.sendAsync(cmd);
        });
    }
    setSharingDisconnect(tags = []) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = 'zCommand Call Sharing Disconnect';
            const type = 'zCommand';
            const topKey = 'CallSharingDisconnectResult';
            const resp = yield this.send(cmd, type, topKey, tags, true, false);
            if (((_a = resp.Status) === null || _a === void 0 ? void 0 : _a.state) !== 'OK')
                this.queryCalls();
            this.queryMute();
        });
    }
    setSharingInstructions(value, tags = []) {
        return __awaiter(this, void 0, void 0, function* () {
            const type = value === 'airPlay' ? 'IOS' : value === 'laptop' ? 'Laptop' : 'None';
            const show = type === 'None' ? 'off' : 'on';
            const cmd = `zCommand Call SetInstructions Show: ${show} Type: ${type}`;
            yield this.send(cmd, 'zCommand', 'CallSharingToggleResult', tags, true);
        });
    }
    setSharingToNormal(tags = []) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const cmd = 'zCommand Call Sharing ToNormal';
            const resp = yield this.send(cmd, 'zCommand', 'CallSharingToggleResult', tags, true, false);
            if (((_a = resp.Status) === null || _a === void 0 ? void 0 : _a.state) === 'OK') {
                this.meetingType = 'NORMAL';
            }
            else {
                this.queryCalls();
            }
        });
    }
    setVideoCameraSelected(id, tags = []) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            // The Video Camera response reports Sync as false. This is a bug in the ZRCSAPI, but we
            // work around it by accepting any value for Sync.
            const cmd = `zConfiguration Video Camera selectedId: ${id}`;
            const type = 'zConfiguration';
            const topKey = 'Video';
            const resp = (yield this.send(cmd, type, topKey, tags, undefined));
            return (_b = (_a = resp.Video) === null || _a === void 0 ? void 0 : _a.Camera) === null || _b === void 0 ? void 0 : _b.selectedId;
        });
    }
    updateContentStateAndSource(sharingInfo, cameraSharingInfo, videoInputs) {
        // Update contentState.
        if (this.findVideoInputId('hdmi', videoInputs)) {
            const hdmiState = sharingInfo.isSharingBlackMagic ? 'sending' : 'none';
            this.state.contentState.update('hdmi', hdmiState);
        }
        else {
            if ('hdmi' in this.state.contentState.values) {
                this.state.contentState.update('hdmi', undefined);
            }
        }
        if (this.findVideoInputId('airPlay', videoInputs)) {
            const airPlayState = sharingInfo.isAirHostClientConnected ? 'sending' : 'none';
            this.state.contentState.update('airPlay', airPlayState);
        }
        else {
            if ('airPlay' in this.state.contentState.values) {
                this.state.contentState.update('airPlay', undefined);
            }
        }
        if (cameraSharingInfo.is_Sharing && cameraSharingInfo.id) {
            const code = `camera.${cameraSharingInfo.id}`;
            if (this.findVideoInputId(code, videoInputs)) {
            }
        }
        let sharingCameraCode;
        for (const videoInput of Object.values(videoInputs)) {
            if (!videoInput.code.startsWith('camera.'))
                continue;
            const isSharing = cameraSharingInfo.is_Sharing &&
                videoInput.code === `camera.${cameraSharingInfo.id}`;
            this.state.contentState.update(videoInput.code, isSharing ? 'sending' : 'none');
            if (isSharing)
                sharingCameraCode = videoInput.code;
        }
        // Update contentSource.
        let contentSource = 'videoConference';
        if (sharingInfo.isSharingBlackMagic) {
            contentSource = 'hdmi';
        }
        else if (sharingInfo.dispState === 'IOS') {
            contentSource = 'airPlay';
        }
        else if (sharingInfo.dispState === 'Laptop') {
            contentSource = 'laptop';
        }
        else if (sharingInfo.dispState === 'None' && sharingCameraCode === undefined) {
            contentSource = 'videoConference';
        }
        else if (sharingCameraCode !== undefined) {
            contentSource = sharingCameraCode;
        }
        if (this.findVideoInputId(contentSource, videoInputs)) {
            this.state.contentSource.update(contentSource);
        }
    }
    updateParticipant(zoomParticipant, participants) {
        if (zoomParticipant.user_id === 0) {
            // SNTY-1327: After a participant leaves a meeting, the Zoom API may generate a spurious
            // asynchronous ListParticipantResult with "event": "ZRCUserChangedEventUserInfoUpdated"
            // and "user_id": 0. Ignore the event if it happens.
            this.log.warning(`ignored ListParticipantsResult with user_id: 0 and event ${zoomParticipant.event}`);
        }
        else {
            if (zoomParticipant.event === 'ZRCUserChangedEventLeftMeeting') {
                const participantId = this.lookupParticipantId(zoomParticipant);
                if (participantId !== undefined) {
                    if (participants) {
                        delete participants[participantId];
                    }
                    else {
                        this.state.callParticipants.updateParticipant(this.zoomPrimaryKey, participantId, undefined);
                    }
                }
            }
            else {
                const [participantId, participant, limit] = this.parseParticipant(zoomParticipant);
                if (!limit) {
                    if (participants) {
                        participants[participantId] = participant;
                    }
                    else {
                        this.state.callParticipants.updateParticipant(this.zoomPrimaryKey, participantId, participant);
                    }
                }
            }
        }
    }
}
// static properties
Zoom.driverType = 'socketDriver';
Zoom.key = '-LDmH6a8aVJTYIXyiiQ9';
Zoom.socketOptions = [
    {
        commandExtractor: (buffer) => {
            let n = 0;
            while (n < buffer.length && terminators.includes(buffer[n]))
                n++;
            buffer = buffer.slice(n);
            if (buffer.length === 0)
                return undefined;
            n = 0;
            if (buffer[n] === '{') {
                const match = jsonRegExp.exec(buffer);
                if (match === null)
                    return undefined;
                //const command = JSON.parse(match[0]);
                buffer = buffer.substr(match[0].length);
                return { command: match[0], buffer };
            }
            while (n < buffer.length && !terminators.includes(buffer[n]))
                n++;
            if (n >= buffer.length)
                return undefined;
            return { command: buffer.slice(0, n), buffer: buffer.substr(n) };
        },
        parser: (command) => {
            try {
                const resp = JSON.parse(command);
                return resp;
            }
            catch (err) {
                return undefined;
            }
        },
        port: 2244,
        sendTerminator: '\r\n',
        socketType: 'ssh',
    },
];
